import { z } from "zod";

const decimalString = z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/, "Expected a canonical decimal string");
const isoTimestamp = z.string().datetime({ offset: true });
const candidateId = z.string().regex(/^ti_cand_sha256:[0-9a-f]{64}$/);
const snapshotId = z.string().regex(/^ti_snap_sha256:[0-9a-f]{64}$/);
const opportunityId = z.string().regex(/^ti_opp_sha256:[0-9a-f]{64}$/);
const opportunitySnapshotId = z.string().regex(/^ti_opp_snap_sha256:[0-9a-f]{64}$/);
const tradeReference = z.string().regex(/^LH-TI-(?:[0-9A-HJKMNP-TV-Z]{4}-){4}[0-9A-HJKMNP-TV-Z*~$=U]$/);
const setupId = z.string().regex(/^ti_setup_sha256:[0-9a-f]{64}$/);
const setupSnapshotId = z.string().regex(/^ti_setup_snap_sha256:[0-9a-f]{64}$/);
const evidenceCutId = z.string().regex(/^ti_cut_sha256:[0-9a-f]{64}$/);

function numeric(value: string) {
  return Number(value);
}

function approximatelyEqual(left: string | number, right: string | number, tolerance = 0.0001) {
  return Math.abs(Number(left) - Number(right)) <= tolerance;
}


export const readinessSchema = z.enum([
  "CRITERIA_MET",
  "CONDITIONS_PENDING",
  "UNAVAILABLE",
  "INELIGIBLE",
]);

export const evidenceQualitySchema = z.enum(["FRESH", "STALE", "INSUFFICIENT", "INCOHERENT"]);

export const strategyFamilySchema = z.enum([
  "long_call",
  "long_put",
  "bull_call_debit",
  "bear_put_debit",
]);

export const naturalOpenSchema = z.object({
  kind: z.literal("NET_DEBIT"),
  value: decimalString,
  currency: z.literal("USD"),
  unit: z.literal("PER_SHARE"),
  basis: z.literal("NATURAL_V1"),
}).strict();

export const tradeIdeaListItemSchema = z.object({
  candidate_id: candidateId,
  ticker: z.string().min(1),
  strategy_family: strategyFamilySchema,
  readiness: readinessSchema,
  evidence_quality: evidenceQualitySchema,
  legs_summary: z.string().min(1),
  natural_open: naturalOpenSchema,
  maximum_loss_usd: decimalString,
  primary_remaining_condition: z.string().nullable().optional(),
  opportunity_expires_at: isoTimestamp,
  observed_at: isoTimestamp,
  snapshot_id: snapshotId,
}).strict();

export const tradeIdeasListSchema = z.object({
  as_of: isoTimestamp,
  scan_run_id: z.string().min(1),
  profile: z.object({ profile_id: z.string().min(1), version: z.string().min(1) }).strict(),
  data_status: z.enum(["COMPLETE", "PARTIAL", "UNAVAILABLE"]),
  market_session: z.enum(["REGULAR_SESSION", "LATEST_COMPLETED_SESSION"]).default("REGULAR_SESSION"),
  partial_reason: z.string().nullable(),
  items: z.array(tradeIdeaListItemSchema),
  next_cursor: z.string().nullable(),
}).strict();

export const opportunityExpressionSchema = z.object({
  candidate_id: candidateId,
  snapshot_id: snapshotId,
  strategy_family: strategyFamilySchema,
  readiness: z.enum(["CRITERIA_MET", "CONDITIONS_PENDING"]),
  legs_summary: z.string().min(1),
  expiry: z.string().date(),
  natural_open: naturalOpenSchema,
  maximum_loss_usd: decimalString,
  primary_remaining_condition: z.string().nullable().optional(),
  observed_at: isoTimestamp,
}).strict();

export const opportunityListItemSchema = z.object({
  opportunity_id: opportunityId,
  opportunity_snapshot_id: opportunitySnapshotId,
  ticker: z.string().min(1),
  direction: z.enum(["BULLISH", "BEARISH"]),
  setup_family: z.literal("DEFINED_RISK_DIRECTIONAL"),
  horizon: z.enum(["DTE_0_30", "DTE_31_60", "DTE_61_120", "DTE_121_PLUS"]),
  readiness: z.enum(["CRITERIA_MET", "CONDITIONS_PENDING"]),
  evidence_quality: z.literal("FRESH"),
  headline: z.string().min(1),
  summary: z.string().min(1),
  expressions: z.array(opportunityExpressionSchema).min(1).max(16),
  recently_changed: z.boolean(),
  changed_fields: z.array(z.string()),
  observed_at: isoTimestamp,
  opportunity_expires_at: isoTimestamp,
}).strict();

export const opportunityDetailSchema = opportunityListItemSchema.extend({
  formation_key: z.string().regex(/^ti_form_sha256:[0-9a-f]{64}$/),
  formation_epoch: z.string().min(1),
  lifecycle: z.enum(["CONFIRMED", "INVALIDATED", "EXPIRED", "ARCHIVED"]),
  policy_versions: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
  disclaimer: z.string().min(1),
}).strict();

export const opportunityListSchema = z.object({
  as_of: isoTimestamp,
  scan_run_id: z.string().min(1),
  profile: z.object({ profile_id: z.string().min(1), version: z.string().min(1) }).strict(),
  data_status: z.enum(["COMPLETE", "PARTIAL", "UNAVAILABLE"]),
  market_session: z.enum(["REGULAR_SESSION", "LATEST_COMPLETED_SESSION"]),
  partial_reason: z.string().nullable(),
  items: z.array(opportunityListItemSchema),
}).strict();

export const houseProfileSchema = z.object({
  schema_version: z.union([
    z.literal("trade_ideas_house_profile_v1"),
    z.literal("trade_ideas_setup_house_profile_v2"),
  ]),
  profile_id: z.string().min(1),
  version: z.string().min(1),
  status: z.enum(["APPROVED", "INTERNAL", "SHADOW", "PROPOSED"]),
  universe_id: z.string().min(1),
  universe_version: z.string().min(1),
  maximum_symbols: z.number().int().positive(),
  strategies: z.record(z.string(), z.object({
    definition_version: z.string(),
    enabled: z.boolean(),
    dte_min: z.number().int().nonnegative(),
    dte_max: z.number().int().nonnegative(),
    ratio: z.string().nullable().optional(),
  }).strict()),
  evidence_policy_version: z.string().min(1),
  pricing_basis: z.literal("NATURAL_V1"),
  threshold_values: z.record(z.string(), z.union([decimalString, z.number().int(), z.boolean()])),
  updated_at: isoTimestamp,
}).strict();

export const tradeReferenceMintResponseSchema = z.object({
  trade_ref: tradeReference,
  opportunity_id: opportunityId,
  opportunity_snapshot_id: opportunitySnapshotId,
  referenced_candidate_id: candidateId.nullable().optional(),
  created_at: isoTimestamp,
  expires_at: isoTimestamp,
  copy_prompt: z.string().min(1),
}).strict();

export const gateFactSchema = z.object({
  gate_id: z.string().min(1),
  outcome: z.enum(["PASS", "FAIL", "UNAVAILABLE"]),
  classification: z.enum(["RECOVERABLE", "PERMANENT", "EVIDENCE"]),
  reason_code: z.string().min(1),
  observed_value: decimalString.nullable().optional(),
  threshold_value: decimalString.nullable().optional(),
  unit: z.string().nullable().optional(),
}).strict();

export const tradeIdeaLegSchema = z.object({
  ordinal: z.number().int().positive(),
  side: z.enum(["LONG", "SHORT"]),
  role: z.string().min(1),
  ratio: z.number().int().positive(),
  right: z.enum(["CALL", "PUT"]),
  expiry: z.string().date(),
  strike: decimalString,
  multiplier: z.number().int().positive(),
  contract: z.string().min(1),
}).strict();

export const tradeIdeaDetailSchema = tradeIdeaListItemSchema.extend({
  lifecycle: z.enum(["ACTIVE", "ARCHIVED", "EXPIRED", "INVALIDATED"]),
  profile: z.object({ profile_id: z.string().min(1), version: z.string().min(1) }).strict(),
  predicate_version: z.string().min(1),
  strategy_definition_version: z.string().min(1),
  pricing_policy_version: z.string().min(1),
  evidence_policy_version: z.string().min(1),
  legs: z.array(tradeIdeaLegSchema).min(1).max(2),
  gates: z.array(gateFactSchema),
  source_labels: z.array(z.string()),
  informational_midpoint_per_share: decimalString.nullable().optional(),
  maximum_profit_at_expiration_usd: decimalString.nullable().optional(),
  evidence: z.object({
    observed_at: isoTimestamp,
    acquired_at: isoTimestamp,
    maximum_source_time_skew_seconds: decimalString,
    generation_sequence: z.number().int().positive(),
  }).strict(),
  fees_excluded: z.literal(true),
  disclaimer: z.string().min(1),
}).strict();

export const compareItemSchema = z.object({
  candidate_id: candidateId,
  ticker: z.string().min(1),
  strategy_family: strategyFamilySchema,
  expiry: z.string().date(),
  readiness: readinessSchema,
  natural_open_per_share: decimalString.nullable(),
  maximum_loss_usd: decimalString.nullable(),
  evidence_observed_at: isoTimestamp,
  remaining_conditions: z.array(z.string()),
}).strict();

export const compareResponseSchema = z.object({
  as_of: isoTimestamp,
  dimensions: z.array(z.enum(["strategy_family", "expiry", "readiness", "natural_open_per_share", "maximum_loss_usd", "remaining_conditions", "evidence_observed_at"])),
  items: z.array(compareItemSchema).min(2).max(10),
  disclaimer: z.string().min(1),
}).strict();

export const subscriptionPreviewSchema = z.object({
  operation: z.literal("CREATE_ONE_SHOT_SUBSCRIPTION"),
  candidate_id: candidateId,
  current_readiness: z.literal("CONDITIONS_PENDING"),
  profile_version: z.string().min(1),
  predicate_version: z.string().min(1),
  snapshot_id: snapshotId,
  checked_about_every_seconds: z.number().int().positive(),
  terminal_outcomes: z.array(z.string().min(1)),
  confirmation_token: z.string().min(32),
  confirmation_expires_at: isoTimestamp,
  ticker: z.string().min(1),
  legs_summary: z.string().min(1),
  opportunity_expires_at: isoTimestamp,
}).strict();

export const ideaSubscriptionSchema = z.object({
  subscription_id: z.string().min(1),
  candidate_id: candidateId,
  ticker: z.string().min(1),
  legs_summary: z.string().min(1),
  profile_version: z.string().min(1),
  predicate_version: z.string().min(1),
  strategy_family: strategyFamilySchema,
  lifecycle: z.enum(["ACTIVE", "COMPLETED", "CANCELLED", "EXPIRED", "INVALIDATED"]),
  created_at: isoTimestamp,
  expires_at: isoTimestamp,
  next_evaluation_at: isoTimestamp,
  terminal_reason: z.string().nullable(),
  completed_at: isoTimestamp.nullable(),
  delivery_state: z.enum(["UNRESERVED", "RESERVED", "PENDING_RETRY", "DELIVERED", "SUPPRESSED", "FAILED_TERMINAL"]),
  last_evaluated_at: isoTimestamp.nullable(),
  evaluation_status: z.enum(["CURRENT", "DELAYED", "DEGRADED", "UNAVAILABLE"]),
}).strict();

export const ideaSubscriptionListSchema = z.object({
  items: z.array(ideaSubscriptionSchema),
}).strict();

export const subscriptionCancelResponseSchema = z.object({
  status: z.enum(["CANCELLED", "ALREADY_CANCELLED"]),
  subscription: ideaSubscriptionSchema,
}).strict();

export const setupWhyNowFactSchema = z.object({
  code: z.string().min(1),
  role: z.enum(["HARD_GATE", "CONFIRMATION", "CONTRADICTION", "CONTEXT"]),
  authority: z.enum(["AUTHORITATIVE", "SHADOW", "DESCRIPTIVE"]),
  summary: z.string().min(1).max(140),
  observed_at: isoTimestamp,
  observed_value: decimalString.nullish(),
  threshold: decimalString.nullish(),
  comparator: z.string().min(1).nullish(),
  unit: z.string().min(1).nullish(),
  policy_id: z.string().min(1).nullish(),
}).strict();

export const setupEvidenceClockSchema = z.enum([
  "COMPLETED_SESSION",
  "EVENT_AS_OF",
  "OPTIONS_FLOW_AS_OF",
  "MACRO_CALENDAR_AS_OF",
  "DECISION_TIME",
]);

export const setupFreshnessSchema = z.object({
  evidence_cut_id: evidenceCutId,
  captured_at: isoTimestamp,
  published_at: isoTimestamp,
  age_seconds: z.number().int().nonnegative(),
  sources: z.array(z.object({
    name: z.string().min(1),
    clock: setupEvidenceClockSchema,
    observed_at: isoTimestamp,
    effective_through: z.string().date(),
  }).strict()),
}).strict();

export const setupProbabilitySchema = z.object({
  availability: z.enum(["AVAILABLE", "UNAVAILABLE"]),
  unavailable_reason: z.string().nullable(),
  cohort_id: z.string().nullable(),
  outcome_definition: z.string().nullable(),
  probability: decimalString.nullable(),
  interval_low: decimalString.nullable(),
  interval_high: decimalString.nullable(),
  sample_size: z.number().int().positive().nullable(),
  calibration_version: z.string().nullable(),
  evaluated_through: z.string().date().nullable(),
}).strict().superRefine((probability, context) => {
  const calibrated = [
    probability.cohort_id,
    probability.outcome_definition,
    probability.probability,
    probability.interval_low,
    probability.interval_high,
    probability.sample_size,
    probability.calibration_version,
    probability.evaluated_through,
  ];
  if (probability.availability === "UNAVAILABLE") {
    if (!probability.unavailable_reason || calibrated.some((value) => value !== null)) {
      context.addIssue({ code: "custom", message: "Unavailable probability must include one reason and no calibrated claim" });
    }
    return;
  }
  if (probability.unavailable_reason !== null || calibrated.some((value) => value === null)) {
    context.addIssue({ code: "custom", message: "Available probability requires complete calibrated cohort facts" });
    return;
  }
  const estimate = Number(probability.probability);
  const low = Number(probability.interval_low);
  const high = Number(probability.interval_high);
  if (!(0 <= low && low <= estimate && estimate <= high && high <= 1)) {
    context.addIssue({ code: "custom", message: "Calibrated probability interval is inconsistent" });
  }
});

export const setupTradeLegSchema = z.object({
  action: z.enum(["BUY", "SELL"]),
  ratio: z.literal(1),
  contract: z.string().min(1),
  expiry: z.string().date(),
  right: z.enum(["CALL", "PUT"]),
  strike: decimalString,
  multiplier: z.literal(100),
}).strict();

export const setupExpressionEventAuthoritySchema = z.object({
  status: z.literal("ELIGIBLE"),
  complete_through: z.string().date(),
  events: z.array(z.object({
    event_type: z.string().min(1),
    event_date: z.string().date(),
    timing: z.string().min(1),
  }).strict()),
  checked_event_types: z.array(z.string().min(1)).min(1),
  risk_window_policy_version: z.literal("expression_event_risk.through_expiry.v1"),
  source_revision: z.string().min(1),
  observed_at: isoTimestamp,
  evaluated_at: isoTimestamp,
}).strict();

export const setupExactTradeSchema = z.object({
  candidate_id: candidateId,
  strategy_family: strategyFamilySchema,
  expiry: z.string().date(),
  legs: z.array(setupTradeLegSchema).min(1).max(2),
  quantity: z.number().int().positive(),
  natural_entry_debit_per_share: decimalString,
  natural_entry_debit_usd: decimalString,
  entry_ceiling_per_share: decimalString,
  reward_risk_entry_ceiling_per_share: decimalString,
  maximum_loss_entry_cap_per_share: decimalString,
  structure_valid_entry_cap_per_share: decimalString,
  evaluated_at: isoTimestamp,
  dte_days: z.number().int().positive(),
  quote_valid_through: isoTimestamp,
  worst_leg_relative_spread: decimalString,
  minimum_leg_open_interest: z.number().int().nonnegative(),
  minimum_leg_volume: z.number().int().nonnegative(),
  execution_policy_version: z.string().min(1),
  maximum_entry_debit_per_share_allowed: decimalString,
  maximum_loss_usd_allowed: decimalString,
  minimum_expiration_target_profit_to_max_loss_ratio_required: decimalString,
  maximum_leg_relative_spread_allowed: decimalString,
  minimum_leg_open_interest_required: z.number().int().nonnegative(),
  minimum_leg_volume_required: z.number().int().nonnegative(),
  event_authority: setupExpressionEventAuthoritySchema,
}).strict().superRefine((trade, context) => {
  if (trade.legs.some((leg) => leg.expiry !== trade.expiry)) {
    context.addIssue({ code: "custom", message: "Exact trade legs must share the published expiry" });
  }
  if (
    trade.event_authority.complete_through < trade.expiry
    || new Date(trade.event_authority.observed_at) > new Date(trade.event_authority.evaluated_at)
    || new Date(trade.event_authority.evaluated_at) > new Date(trade.evaluated_at)
  ) {
    context.addIssue({ code: "custom", message: "Exact trade event authority must be complete through expiry and point-in-time valid" });
  }
  const expiryAtUtcMidnight = Date.parse(`${trade.expiry}T00:00:00Z`);
  const evaluatedDate = new Date(trade.evaluated_at);
  const evaluatedAtUtcMidnight = Date.UTC(
    evaluatedDate.getUTCFullYear(),
    evaluatedDate.getUTCMonth(),
    evaluatedDate.getUTCDate(),
  );
  if ((expiryAtUtcMidnight - evaluatedAtUtcMidnight) / 86_400_000 !== trade.dte_days) {
    context.addIssue({ code: "custom", message: "Exact trade DTE must match expiry and evaluation date" });
  }
  if (Number(trade.worst_leg_relative_spread) < 0) {
    context.addIssue({ code: "custom", message: "Worst-leg spread cannot be negative" });
  }
  if (
    Number(trade.maximum_entry_debit_per_share_allowed) <= 0
    || Number(trade.maximum_loss_usd_allowed) <= 0
    || Number(trade.minimum_expiration_target_profit_to_max_loss_ratio_required) <= 0
    || Number(trade.maximum_leg_relative_spread_allowed) <= 0
  ) {
    context.addIssue({ code: "custom", message: "Execution policy thresholds must be positive" });
  }
  if (
    Number(trade.natural_entry_debit_per_share) > Number(trade.maximum_entry_debit_per_share_allowed)
    || Number(trade.entry_ceiling_per_share) > Number(trade.maximum_entry_debit_per_share_allowed)
    || Number(trade.natural_entry_debit_usd) > Number(trade.maximum_loss_usd_allowed)
    || Number(trade.worst_leg_relative_spread) > Number(trade.maximum_leg_relative_spread_allowed)
    || trade.minimum_leg_open_interest < trade.minimum_leg_open_interest_required
    || trade.minimum_leg_volume < trade.minimum_leg_volume_required
  ) {
    context.addIssue({ code: "custom", message: "Ready exact trade does not pass its published execution policy" });
  }
  if (new Date(trade.quote_valid_through).getTime() <= new Date(trade.evaluated_at).getTime()) {
    context.addIssue({ code: "custom", message: "Exact trade quote validity must extend beyond its evaluation time" });
  }
  const expected = {
    long_call: { count: 1, right: "CALL", actions: ["BUY"] },
    long_put: { count: 1, right: "PUT", actions: ["BUY"] },
    bull_call_debit: { count: 2, right: "CALL", actions: ["BUY", "SELL"] },
    bear_put_debit: { count: 2, right: "PUT", actions: ["BUY", "SELL"] },
  }[trade.strategy_family];
  if (
    trade.legs.length !== expected.count
    || trade.legs.some((leg, index) => leg.right !== expected.right || leg.action !== expected.actions[index])
    || trade.legs.some((leg) => leg.multiplier !== trade.legs[0].multiplier || leg.ratio !== trade.legs[0].ratio)
  ) {
    context.addIssue({ code: "custom", message: "Exact trade legs do not match the published strategy" });
  }
  if (trade.legs.length === 2) {
    const [longLeg, shortLeg] = trade.legs;
    const ordered = trade.strategy_family === "bull_call_debit"
      ? numeric(longLeg.strike) < numeric(shortLeg.strike)
      : numeric(longLeg.strike) > numeric(shortLeg.strike);
    if (!ordered) context.addIssue({ code: "custom", message: "Vertical strikes are not ordered for the published strategy" });
  }
  const expectedDebit = numeric(trade.natural_entry_debit_per_share)
    * trade.quantity
    * (trade.legs[0]?.multiplier ?? 0);
  if (!approximatelyEqual(trade.natural_entry_debit_usd, expectedDebit)) {
    context.addIssue({ code: "custom", message: "Exact trade total debit must equal debit per share times quantity and multiplier" });
  }
  if (numeric(trade.entry_ceiling_per_share) < numeric(trade.natural_entry_debit_per_share)) {
    context.addIssue({ code: "custom", message: "Exact trade entry ceiling cannot be below its quoted debit" });
  }
  const effectiveCeiling = Math.min(
    numeric(trade.reward_risk_entry_ceiling_per_share),
    numeric(trade.maximum_entry_debit_per_share_allowed),
    numeric(trade.maximum_loss_entry_cap_per_share),
    numeric(trade.structure_valid_entry_cap_per_share),
  );
  if (!approximatelyEqual(trade.entry_ceiling_per_share, effectiveCeiling)) {
    context.addIssue({ code: "custom", message: "Exact trade entry ceiling must equal the most restrictive published risk cap" });
  }
  const expectedMaximumLossCap = numeric(trade.maximum_loss_usd_allowed)
    / (trade.quantity * (trade.legs[0]?.multiplier ?? 0));
  if (!approximatelyEqual(trade.maximum_loss_entry_cap_per_share, expectedMaximumLossCap)) {
    context.addIssue({ code: "custom", message: "Maximum-loss entry cap must match quantity and contract multiplier" });
  }
  const expectedStructureCap = trade.legs.length === 2
    ? Math.abs(numeric(trade.legs[1].strike) - numeric(trade.legs[0].strike))
    : numeric(trade.maximum_entry_debit_per_share_allowed);
  if (!approximatelyEqual(trade.structure_valid_entry_cap_per_share, expectedStructureCap)) {
    context.addIssue({ code: "custom", message: "Structure-valid entry cap does not match the exact strategy" });
  }
});

type SetupExactTrade = z.infer<typeof setupExactTradeSchema>;

function expirationEconomics(trade: SetupExactTrade, underlying: string, entryDebitUsd?: number) {
  const price = numeric(underlying);
  const debit = entryDebitUsd ?? numeric(trade.natural_entry_debit_usd);
  const contracts = trade.quantity * trade.legs[0].multiplier;
  const [longLeg, shortLeg] = trade.legs;
  const longStrike = numeric(longLeg.strike);
  let intrinsicPerShare = 0;
  if (trade.strategy_family === "long_call") intrinsicPerShare = Math.max(price - longStrike, 0);
  if (trade.strategy_family === "long_put") intrinsicPerShare = Math.max(longStrike - price, 0);
  if (trade.strategy_family === "bull_call_debit" && shortLeg) {
    intrinsicPerShare = Math.min(Math.max(price - longStrike, 0), numeric(shortLeg.strike) - longStrike);
  }
  if (trade.strategy_family === "bear_put_debit" && shortLeg) {
    intrinsicPerShare = Math.min(Math.max(longStrike - price, 0), longStrike - numeric(shortLeg.strike));
  }
  return intrinsicPerShare * contracts - debit;
}

export const setupPayoffSchema = z.object({
  target_profit_usd: decimalString,
  loss_at_invalidation_usd: decimalString,
  maximum_loss_usd: decimalString,
  maximum_profit_usd: decimalString.nullable(),
  maximum_profit_unlimited: z.boolean(),
  /** @deprecated Use expiration_target_profit_to_max_loss_ratio. */
  target_reward_risk: decimalString,
  expiration_target_profit_to_max_loss_ratio: decimalString,
  expiration_target_profit_to_invalidation_loss_ratio: decimalString.nullable(),
  breakeven_underlying: decimalString,
  limit_entry_debit_usd: decimalString,
  limit_maximum_loss_usd: decimalString,
  limit_maximum_profit_usd: decimalString.nullable(),
  limit_target_profit_usd: decimalString,
  limit_invalidation_loss_usd: decimalString,
  limit_expiration_target_profit_to_max_loss_ratio: decimalString,
  limit_breakeven_underlying: decimalString,
  payoff_basis: z.literal("EXPIRATION_INTRINSIC_V1"),
  valuation_time: z.literal("EXPIRATION"),
  target_date_estimate_available: z.literal(false),
}).strict().superRefine((payoff, context) => {
  const positive = [
    payoff.target_profit_usd,
    payoff.maximum_loss_usd,
    payoff.expiration_target_profit_to_max_loss_ratio,
    payoff.limit_entry_debit_usd,
    payoff.limit_maximum_loss_usd,
    payoff.limit_target_profit_usd,
    payoff.limit_expiration_target_profit_to_max_loss_ratio,
  ];
  if (positive.some((value) => Number(value) <= 0) || Number(payoff.loss_at_invalidation_usd) < 0) {
    context.addIssue({ code: "custom", message: "Published payoff must contain positive bounded economics" });
  }
  if (payoff.maximum_profit_unlimited !== (payoff.maximum_profit_usd === null)) {
    context.addIssue({ code: "custom", message: "Maximum-profit availability is inconsistent" });
  }
  if (payoff.maximum_profit_unlimited !== (payoff.limit_maximum_profit_usd === null)) {
    context.addIssue({ code: "custom", message: "Limit-basis maximum-profit availability is inconsistent" });
  }
  if (Number(payoff.target_reward_risk) !== Number(payoff.expiration_target_profit_to_max_loss_ratio)) {
    context.addIssue({ code: "custom", message: "Deprecated target reward/risk must match profit-to-max-loss ratio" });
  }
  const invalidationRatio = payoff.expiration_target_profit_to_invalidation_loss_ratio;
  if ((Number(payoff.loss_at_invalidation_usd) === 0) !== (invalidationRatio === null)) {
    context.addIssue({ code: "custom", message: "Invalidation-loss ratio availability is inconsistent" });
  }
  if (invalidationRatio !== null && Number(invalidationRatio) <= 0) {
    context.addIssue({ code: "custom", message: "Invalidation-loss ratio must be positive" });
  }
  const equalsRoundedRatio = (published: string, numerator: string, denominator: string) => {
    const expected = Math.round((Number(numerator) / Number(denominator) + Number.EPSILON) * 10_000) / 10_000;
    return Math.abs(Number(published) - expected) < 0.0000001;
  };
  if (!equalsRoundedRatio(payoff.expiration_target_profit_to_max_loss_ratio, payoff.target_profit_usd, payoff.maximum_loss_usd)) {
    context.addIssue({ code: "custom", message: "Profit-to-max-loss ratio does not match the published payoff" });
  }
  if (!equalsRoundedRatio(payoff.limit_expiration_target_profit_to_max_loss_ratio, payoff.limit_target_profit_usd, payoff.limit_maximum_loss_usd)) {
    context.addIssue({ code: "custom", message: "Limit-basis profit-to-max-loss ratio does not match the published payoff" });
  }
  if (invalidationRatio !== null && !equalsRoundedRatio(invalidationRatio, payoff.target_profit_usd, payoff.loss_at_invalidation_usd)) {
    context.addIssue({ code: "custom", message: "Profit-to-invalidation-loss ratio does not match the published payoff" });
  }
});

export const setupReadyAlternativeSchema = z.object({
  exact_trade: setupExactTradeSchema,
  payoff: setupPayoffSchema,
}).strict();

export const internalExpressionSelectionSchema = z.object({
  schema_version: z.literal("trade_idea_internal_expression_selection.v1"),
  state: z.enum(["SELECTED", "ABSTAIN"]),
  primary_candidate_id: candidateId.nullable(),
  alternative_candidate_id: candidateId.nullable(),
  tradeoff_code: z.enum([
    "LOWER_MAXIMUM_LOSS_VS_ALTERNATIVE",
    "DEFINED_WIDTH_VS_SINGLE_LEG_EXPOSURE",
    "SINGLE_LEG_EXPOSURE_VS_DEFINED_WIDTH",
    "SAME_FAMILY_STABLE_TIEBREAK",
  ]).nullable(),
  considered_candidate_ids: z.array(candidateId),
  policy_version: z.literal("internal_expression_selection.maximum_loss_then_defined_width.v1"),
  policy_hash: z.string().regex(/^[0-9a-f]{64}$/),
  evidence_hash: z.string().regex(/^[0-9a-f]{64}$/),
  reason_code: z.enum(["HOUSE_MANDATE_APPLIED", "NO_ELIGIBLE_EXPRESSION", "SELECTOR_FAILED_CLOSED"]),
}).strict().superRefine((selection, context) => {
  const considered = [...selection.considered_candidate_ids].sort();
  if (new Set(considered).size !== considered.length || considered.some((id, index) => id !== selection.considered_candidate_ids[index])) {
    context.addIssue({ code: "custom", message: "Internal selection considered ids must be unique and sorted" });
  }
  if (selection.state === "ABSTAIN") {
    if (selection.primary_candidate_id !== null || selection.alternative_candidate_id !== null || selection.tradeoff_code !== null) {
      context.addIssue({ code: "custom", message: "Internal abstention cannot select an expression" });
    }
    return;
  }
  if (selection.primary_candidate_id === null || !selection.considered_candidate_ids.includes(selection.primary_candidate_id)) {
    context.addIssue({ code: "custom", message: "Internal primary must belong to the considered set" });
  }
  if ((selection.alternative_candidate_id === null) !== (selection.tradeoff_code === null)) {
    context.addIssue({ code: "custom", message: "Internal alternative and tradeoff must be paired" });
  }
  if (selection.alternative_candidate_id !== null && (
    selection.alternative_candidate_id === selection.primary_candidate_id
    || !selection.considered_candidate_ids.includes(selection.alternative_candidate_id)
  )) {
    context.addIssue({ code: "custom", message: "Internal alternative must be a different considered expression" });
  }
});

export const setupEvidenceCohortSchema = z.object({
  cohort: z.enum(["CONFIRMING_ELEVATED", "CONFIRMING_ORDINARY", "TECHNICAL_ONLY", "MIXED", "CONTRADICTORY"]),
  policy_version: z.literal("trade_ideas.descriptive_evidence_cohort.v1"),
  flow_state: z.enum(["ALIGNED", "OPPOSED", "MIXED", "NEUTRAL", "NOT_ASSESSED"]),
  flow_health: z.enum(["COMPLETE", "PARTIAL", "FAILED", "UNAVAILABLE"]),
  activity_level: z.string().nullable(),
  baseline_sample_sessions: z.number().int().nonnegative().nullable(),
  directional_evidence_codes: z.array(z.string()),
  descriptive_only: z.literal(true),
}).strict();

export const setupRowSchema = z.object({
  schema_version: z.literal("trade_idea_setup_row.v9"),
  state: z.enum(["WATCH", "READY", "ENTRY_PENDING", "UNAVAILABLE", "HIDDEN"]),
  visible: z.boolean(),
  setup_id: setupId,
  setup_snapshot_id: setupSnapshotId,
  ticker: z.string().min(1),
  setup_family: z.enum(["TREND_BREAKOUT", "PULLBACK_TO_PRIOR_REACTION", "FAILED_BREAKOUT_REVERSAL"]),
  playbook_version: z.string().min(1).optional().default("1.0.0"),
  horizon: z.enum(["TACTICAL", "SWING", "POSITION"]).optional().default("SWING"),
  horizon_policy_version: z.string().min(1).optional().default("setup_horizon.swing.v1"),
  formation_epoch: z.number().int().positive().optional().default(1),
  direction: z.enum(["BULLISH", "BEARISH"]),
  why_now: z.array(setupWhyNowFactSchema).max(3),
  trigger: decimalString,
  target: decimalString,
  invalidation: decimalString,
  expected_resolution_sessions: z.number().int().positive(),
  expires_after_session: z.string().date(),
  exact_trade: setupExactTradeSchema.nullable(),
  payoff: setupPayoffSchema.nullable(),
  event: z.object({
    state: z.enum(["CLEAR", "BLOCKED", "UNAVAILABLE"]),
    complete_through: z.string().date(),
    event_types: z.array(z.string()),
    checked_event_types: z.array(z.string()),
    risk_window_policy_version: z.string().nullable(),
  }).strict(),
  flow: z.object({
    state: z.enum(["ALIGNED", "OPPOSED", "MIXED", "NEUTRAL", "NOT_ASSESSED"]),
    health: z.enum(["COMPLETE", "PARTIAL", "FAILED", "UNAVAILABLE"]),
    availability: z.enum(["AVAILABLE", "PARTIAL", "STALE", "UNAVAILABLE"]),
    summary: z.string().min(1).max(240),
    evidence_codes: z.array(z.string()),
    authority: z.enum(["SHADOW", "DESCRIPTIVE"]),
    window: z.enum(["FIVE_MINUTES", "THIRTY_MINUTES", "SESSION"]),
    source_revision: z.string().min(1),
    assessment_policy_version: z.string().min(1),
    snapshot_id: z.string().min(1).nullable(),
    as_of: isoTimestamp.nullable(),
    requested_at: isoTimestamp.nullable(),
    observed_from: isoTimestamp.nullable(),
    observed_through: isoTimestamp.nullable(),
    fresh_through: isoTimestamp.nullable(),
    context_valid_through: isoTimestamp.nullable(),
    replay_integrity: z.enum(["AS_KNOWN_AT", "LEGACY_ACTIVITY_ONLY"]).nullable(),
    session_id: z.string().nullable(),
    session_scope: z.enum(["LIVE_SESSION", "LAST_COMPLETED_SESSION"]).nullable(),
    session_policy: z.enum(["STANDARD_1600", "EXTENDED_1615"]).nullable(),
    market_calendar_version: z.string().nullable(),
    aligned_dte_min: z.number().int().nonnegative().nullable(),
    aligned_dte_max: z.number().int().nonnegative().nullable(),
    observed_unique_print_count: z.number().int().nonnegative().nullable(),
    observed_premium_cents: z.number().int().nonnegative().nullable(),
    activity_print_count: z.number().int().nonnegative().nullable(),
    activity_premium_cents: z.number().int().nonnegative().nullable(),
    call_print_count: z.number().int().nonnegative().nullable(),
    put_print_count: z.number().int().nonnegative().nullable(),
    quote_coverage_bps: z.number().int().min(0).max(10_000).nullable(),
    classification_coverage_bps: z.number().int().min(0).max(10_000).nullable(),
    qualifying_print_count: z.number().int().nonnegative().nullable(),
    qualifying_distinct_contract_count: z.number().int().nonnegative().nullable(),
    largest_qualifying_print_premium_cents: z.number().int().nonnegative().nullable(),
    largest_qualifying_print_share_bps: z.number().int().min(0).max(10_000).nullable(),
    dte_coverage_bps: z.number().int().min(0).max(10_000).nullable(),
    aligned_dte_print_count: z.number().int().nonnegative().nullable(),
    aligned_dte_premium_cents: z.number().int().nonnegative().nullable(),
    unusual_print_count: z.number().int().nonnegative().nullable(),
    sweep_print_count: z.number().int().nonnegative().nullable(),
    zero_dte_print_count: z.number().int().nonnegative().nullable(),
    activity_percentile_bps: z.number().int().min(0).max(10_000).nullable(),
    premium_percentile_bps: z.number().int().min(0).max(10_000).nullable(),
    print_count_percentile_bps: z.number().int().min(0).max(10_000).nullable(),
    baseline_robust_zscore_milli: z.number().int().nullable(),
    activity_level: z.enum(["ORDINARY", "ELEVATED", "EXCEPTIONAL"]).nullable(),
    baseline_id: z.string().regex(/^mof_base_sha256:[0-9a-f]{64}$/).nullable(),
    baseline_mode: z.enum(["FINALIZED_HISTORY", "AS_KNOWN_AT"]).nullable(),
    baseline_as_of: isoTimestamp.nullable(),
    baseline_correction_lag_sessions: z.number().int().min(1).max(10).nullable(),
    baseline_session_phase_minute: z.number().int().min(0).max(405).nullable(),
    baseline_dte_min: z.number().int().nonnegative().nullable(),
    baseline_dte_max: z.number().int().nonnegative().nullable(),
    baseline_sample_sessions: z.number().int().min(20).max(120).nullable(),
    baseline_evaluated_through: z.string().date().nullable(),
    baseline_version: z.string().min(1).nullable(),
    excluded_ambiguous_count: z.number().int().nonnegative(),
    excluded_complex_count: z.number().int().nonnegative(),
    excluded_unknown_condition_count: z.number().int().nonnegative(),
    excluded_correction_count: z.number().int().nonnegative(),
    reason_code: z.string().nullable(),
    retryable: z.boolean(),
  }).strict(),
  macro: z.object({
    state: z.enum(["NOT_APPLICABLE", "CLEAR", "BLOCKED", "UNAVAILABLE"]),
    policy_version: z.string().min(1),
    assessed_at: isoTimestamp.nullable(),
    snapshot_id: z.string().min(1).nullable(),
    source_revision: z.string().min(1).nullable(),
    source_observed_at: isoTimestamp.nullable(),
    source_acquired_at: isoTimestamp.nullable(),
    source_materialized_at: isoTimestamp.nullable(),
    source_fresh_through: isoTimestamp.nullable(),
    source_age_seconds: z.number().int().nonnegative().nullable(),
    coverage_end: z.string().date().nullable(),
    entry_window_start: z.string().date().nullable(),
    entry_window_end: z.string().date().nullable(),
    context_window_end: z.string().date().nullable(),
    events: z.array(z.object({
      release_id: z.number().int().positive(),
      release_name: z.string().min(1),
      effective_date: z.string().date(),
      entry_blocking: z.boolean(),
    }).strict()),
    reason_code: z.string().nullable(),
  }).strict().superRefine((macro, context) => {
    const provenance = [
      macro.source_revision,
      macro.source_observed_at,
      macro.source_acquired_at,
      macro.source_materialized_at,
      macro.source_fresh_through,
      macro.source_age_seconds,
    ];
    if (macro.snapshot_id === null) {
      if (provenance.some((value) => value !== null)) {
        context.addIssue({ code: "custom", message: "Macro provenance requires an immutable snapshot" });
      }
      return;
    }
    if (macro.assessed_at === null || provenance.some((value) => value === null)) {
      context.addIssue({ code: "custom", message: "Macro snapshot requires complete source clocks and revision" });
      return;
    }
    const observed = Date.parse(macro.source_observed_at!);
    const acquired = Date.parse(macro.source_acquired_at!);
    const materialized = Date.parse(macro.source_materialized_at!);
    const assessed = Date.parse(macro.assessed_at);
    const fresh = Date.parse(macro.source_fresh_through!);
    if (!(observed <= acquired && acquired <= materialized && materialized <= assessed)) {
      context.addIssue({ code: "custom", message: "Macro source clocks must precede the decision" });
    }
    if (macro.state !== "UNAVAILABLE" && assessed > fresh) {
      context.addIssue({ code: "custom", message: "Complete macro evidence cannot be stale" });
    }
    if (macro.source_age_seconds !== Math.floor((assessed - observed) / 1_000)) {
      context.addIssue({ code: "custom", message: "Macro evidence age must match its source clock" });
    }
  }),
  underlying_quote: z.object({
    observation_id: z.string().regex(/^muq_sha256:[0-9a-f]{64}$/),
    basis: z.literal("NBBO_MIDPOINT"),
    bid_price_micros: z.number().int().positive(),
    ask_price_micros: z.number().int().positive(),
    midpoint_price_micros: z.number().int().positive(),
    relative_spread_bps: z.number().int().min(0).max(10_000),
    bid_size: z.number().int().positive(),
    ask_size: z.number().int().positive(),
    event_time: isoTimestamp,
    received_at: isoTimestamp,
    materialized_at: isoTimestamp,
    fresh_through: isoTimestamp,
    source_revision: z.string().min(1),
    spread_policy_version: z.string().min(1),
    session_id: z.string().min(1),
  }).strict().nullable(),
  freshness: setupFreshnessSchema,
  probability: setupProbabilitySchema,
  evidence_cohort: setupEvidenceCohortSchema,
  alert_eligibility: z.object({
    status: z.enum(["MONITORABLE", "NOT_MONITORABLE"]),
    reason: z.enum(["ACTIVATION_PENDING", "ENTRY_REEVALUATION", "NEW_SETUP_REQUIRED", "REFRESH_REQUIRED", "STATE_NOT_PENDING"]),
  }).strict(),
  state_reasons: z.array(z.string()),
  pending_reasons: z.array(z.object({
    code: z.enum(["TRIGGER_PENDING", "ENTRY_CHASED", "EXACT_ENTRY_PENDING", "QUOTE_VALIDITY_EXPIRED", "QUOTE_EVIDENCE_SCHEMA_OUTDATED", "ENTRY_EVIDENCE_UNAVAILABLE", "MACRO_EVENT_IN_ENTRY_WINDOW", "MACRO_EVIDENCE_UNAVAILABLE"]),
    condition: z.enum(["UNDERLYING_ACTIVATION", "ENTRY_LOCATION", "EXACT_EXPRESSION", "QUOTE_FRESHNESS", "EVIDENCE_AVAILABILITY", "EVENT_RISK"]),
    recoverability: z.enum(["MONITORABLE", "REEVALUATE", "NEW_SETUP_REQUIRED"]),
  }).strict()),
  alternatives: z.array(setupReadyAlternativeSchema).max(4),
  primary_expression_id: z.null(),
}).strict().superRefine((row, context) => {
  const completeTrade = row.exact_trade !== null && row.payoff !== null;
  const alternativeTrades = row.alternatives.length > 0;
  const trigger = numeric(row.trigger);
  const target = numeric(row.target);
  const invalidation = numeric(row.invalidation);
  const validGeometry = row.direction === "BULLISH"
    ? invalidation < trigger && trigger < target
    : target < trigger && trigger < invalidation;
  if (!validGeometry) {
    context.addIssue({ code: "custom", message: "Setup levels do not match the published direction" });
  }
  if ((row.state === "READY") !== (completeTrade || alternativeTrades)) {
    context.addIssue({ code: "custom", message: "Ready state requires eligible exact-expression economics" });
  }
  if (completeTrade && alternativeTrades) {
    context.addIssue({ code: "custom", message: "Ready alternatives cannot hide a primary expression" });
  }
  if (row.state === "WATCH" && (row.exact_trade !== null || alternativeTrades)) {
    context.addIssue({ code: "custom", message: "Watch state cannot promise option strikes" });
  }
  if (row.visible !== !["HIDDEN", "UNAVAILABLE"].includes(row.state)) {
    context.addIssue({ code: "custom", message: "Setup visibility is inconsistent with its state" });
  }
  if (row.visible && row.why_now.length === 0) {
    context.addIssue({ code: "custom", message: "Visible setups require why-now evidence" });
  }
  if ((row.exact_trade === null) !== (row.payoff === null)) {
    context.addIssue({ code: "custom", message: "Exact trade and payoff must be published together" });
  }
  const readyPairs = completeTrade
    ? [{ exact_trade: row.exact_trade!, payoff: row.payoff! }]
    : row.alternatives;
  if (new Set(readyPairs.map((item) => item.exact_trade.candidate_id)).size !== readyPairs.length) {
    context.addIssue({ code: "custom", message: "Ready alternatives must be unique" });
  }
  const flowClocks = [row.flow.as_of, row.flow.requested_at, row.flow.observed_from, row.flow.observed_through, row.flow.fresh_through, row.flow.context_valid_through];
  if (flowClocks.some((value) => value === null) !== flowClocks.every((value) => value === null)) {
    context.addIssue({ code: "custom", message: "Flow evidence clocks must be complete or absent" });
  }
  if (row.flow.health !== "COMPLETE" && row.flow.state !== "NOT_ASSESSED") {
    context.addIssue({ code: "custom", message: "Incomplete flow cannot assert directional meaning" });
  }
  if (row.flow.state === "NOT_ASSESSED" && row.flow.reason_code === null) {
    context.addIssue({ code: "custom", message: "Unassessed flow requires a reason" });
  }
  if (row.flow.state !== "NOT_ASSESSED" && (row.flow.as_of === null || row.flow.aligned_dte_min === null)) {
    context.addIssue({ code: "custom", message: "Assessed flow requires clocks and an aligned DTE cohort" });
  }
  const flowSession = [row.flow.snapshot_id, row.flow.replay_integrity, row.flow.session_id, row.flow.session_scope, row.flow.session_policy, row.flow.market_calendar_version];
  if (row.flow.as_of !== null && flowSession.some((value) => value === null)) {
    context.addIssue({ code: "custom", message: "Assessed flow requires replay and session identity" });
  }
  if (row.flow.state === "NEUTRAL" && (
    row.flow.health !== "COMPLETE"
    || row.flow.aligned_dte_print_count !== 0
  )) {
    context.addIssue({ code: "custom", message: "Neutral flow requires a complete zero-print aligned-DTE cut" });
  }
  if (
    row.evidence_cohort.flow_state !== row.flow.state
    || row.evidence_cohort.flow_health !== row.flow.health
    || row.evidence_cohort.activity_level !== row.flow.activity_level
    || row.evidence_cohort.baseline_sample_sessions !== row.flow.baseline_sample_sessions
  ) {
    context.addIssue({ code: "custom", message: "Evidence cohort must bind the exact published flow facts" });
  }
  if (row.evidence_cohort.cohort === "CONFIRMING_ELEVATED" && (
    row.flow.state !== "ALIGNED"
    || row.flow.health !== "COMPLETE"
    || !["ELEVATED", "EXCEPTIONAL"].includes(row.flow.activity_level ?? "")
    || (row.flow.baseline_sample_sessions ?? 0) < 20
  )) {
    context.addIssue({ code: "custom", message: "Elevated confirmation requires mature complete aligned evidence" });
  }
  if (row.state === "READY" && row.underlying_quote === null) {
    context.addIssue({ code: "custom", message: "Ready setup requires the exact fresh underlying quote used for activation" });
  }
  if (row.state === "READY" && !["CLEAR", "NOT_APPLICABLE"].includes(row.macro.state)) {
    context.addIssue({ code: "custom", message: "Ready setup cannot carry blocked or unavailable macro entry risk" });
  }
  if (row.macro.state === "NOT_APPLICABLE" && (
    row.macro.assessed_at !== null
    || row.macro.snapshot_id !== null
    || row.macro.events.length > 0
  )) {
    context.addIssue({ code: "custom", message: "Not-applicable macro context cannot carry an assessment" });
  }
  if (["CLEAR", "BLOCKED"].includes(row.macro.state) && (
    row.macro.assessed_at === null
    || row.macro.snapshot_id === null
    || row.macro.coverage_end === null
    || row.macro.entry_window_end === null
  )) {
    context.addIssue({ code: "custom", message: "Complete macro context requires exact snapshot and window coverage" });
  }
  if (row.underlying_quote !== null) {
    const quote = row.underlying_quote;
    if (
      quote.ask_price_micros <= quote.bid_price_micros
      || quote.midpoint_price_micros !== Math.floor((quote.bid_price_micros + quote.ask_price_micros) / 2)
      || new Date(quote.event_time) > new Date(quote.received_at)
      || new Date(quote.received_at) > new Date(quote.materialized_at)
      || new Date(quote.materialized_at) >= new Date(quote.fresh_through)
    ) {
      context.addIssue({ code: "custom", message: "Underlying NBBO fact is internally incoherent" });
    }
  }
  if (
    (row.flow.observed_unique_print_count === null) !== (row.flow.observed_premium_cents === null)
    || (
      row.flow.observed_unique_print_count !== null
      && row.flow.activity_print_count !== null
      && row.flow.observed_unique_print_count < row.flow.activity_print_count
    )
    || (
      row.flow.observed_premium_cents !== null
      && row.flow.activity_premium_cents !== null
      && row.flow.observed_premium_cents < row.flow.activity_premium_cents
    )
  ) {
    context.addIssue({ code: "custom", message: "Raw and eligible flow activity facts are inconsistent" });
  }
  const flowBaseline = [
    row.flow.activity_percentile_bps,
    row.flow.premium_percentile_bps,
    row.flow.print_count_percentile_bps,
    row.flow.activity_level,
    row.flow.baseline_id,
    row.flow.baseline_mode,
    row.flow.baseline_as_of,
    row.flow.baseline_correction_lag_sessions,
    row.flow.baseline_dte_min,
    row.flow.baseline_dte_max,
    row.flow.baseline_sample_sessions,
    row.flow.baseline_evaluated_through,
    row.flow.baseline_version,
  ];
  if (flowBaseline.some((value) => value === null) !== flowBaseline.every((value) => value === null)) {
    context.addIssue({ code: "custom", message: "Flow activity baseline facts must be complete or absent" });
  }
  if (
    row.flow.activity_percentile_bps !== null
    && row.flow.activity_percentile_bps !== row.flow.premium_percentile_bps
  ) {
    context.addIssue({ code: "custom", message: "Flow premium percentile alias is inconsistent" });
  }
  if (row.state === "READY" && row.exact_trade && (
    row.exact_trade.event_authority.status !== "ELIGIBLE"
    || row.exact_trade.event_authority.complete_through < row.exact_trade.expiry
    || row.exact_trade.event_authority.risk_window_policy_version !== "expression_event_risk.through_expiry.v1"
  )) {
    context.addIssue({ code: "custom", message: "Ready event coverage must be clear through the exact trade expiry" });
  }
  if (
    row.state === "READY"
    && row.exact_trade
    && row.payoff
    && (
      Number(row.payoff.expiration_target_profit_to_max_loss_ratio)
        < Number(row.exact_trade.minimum_expiration_target_profit_to_max_loss_ratio_required)
      || Number(row.payoff.maximum_loss_usd) > Number(row.exact_trade.maximum_loss_usd_allowed)
      || Number(row.payoff.limit_expiration_target_profit_to_max_loss_ratio)
        < Number(row.exact_trade.minimum_expiration_target_profit_to_max_loss_ratio_required)
      || Number(row.payoff.limit_maximum_loss_usd) > Number(row.exact_trade.maximum_loss_usd_allowed)
    )
  ) {
    context.addIssue({ code: "custom", message: "Ready payoff does not pass its published risk/reward requirements" });
  }
  if (row.state === "READY" && row.exact_trade && row.payoff) {
    const bullishStrategy = ["long_call", "bull_call_debit"].includes(row.exact_trade.strategy_family);
    if (bullishStrategy !== (row.direction === "BULLISH")) {
      context.addIssue({ code: "custom", message: "Exact trade strategy does not match the setup direction" });
    }
    const trade = row.exact_trade;
    const payoff = row.payoff;
    // EXPIRATION_INTRINSIC_V1 only authorizes standard 1:1, 100-share
    // contracts. Refuse unsupported sizing before recomputing any economics.
    if (trade.legs.some((leg) => leg.ratio !== 1 || leg.multiplier !== 100)) {
      context.addIssue({ code: "custom", message: "Approved v1 payoff requires 1:1 legs with a 100-share multiplier" });
      return;
    }
    const [longLeg, shortLeg] = trade.legs;
    const contracts = trade.quantity * longLeg.multiplier;
    const debit = numeric(trade.natural_entry_debit_usd);
    const longStrike = numeric(longLeg.strike);
    const targetProfit = expirationEconomics(trade, row.target);
    const invalidationLoss = Math.max(-expirationEconomics(trade, row.invalidation), 0);
    const breakeven = ["long_call", "bull_call_debit"].includes(trade.strategy_family)
      ? longStrike + numeric(trade.natural_entry_debit_per_share)
      : longStrike - numeric(trade.natural_entry_debit_per_share);
    const maximumProfit = shortLeg
      ? Math.abs(numeric(shortLeg.strike) - longStrike) * contracts - debit
      : trade.strategy_family === "long_put"
        ? longStrike * contracts - debit
        : null;
    const limitDebit = numeric(trade.entry_ceiling_per_share) * contracts;
    const limitTargetProfit = expirationEconomics(trade, row.target, limitDebit);
    const limitInvalidationLoss = Math.max(-expirationEconomics(trade, row.invalidation, limitDebit), 0);
    const limitBreakeven = ["long_call", "bull_call_debit"].includes(trade.strategy_family)
      ? longStrike + numeric(trade.entry_ceiling_per_share)
      : longStrike - numeric(trade.entry_ceiling_per_share);
    const limitMaximumProfit = shortLeg
      ? Math.abs(numeric(shortLeg.strike) - longStrike) * contracts - limitDebit
      : trade.strategy_family === "long_put"
        ? longStrike * contracts - limitDebit
        : null;
    if (
      !approximatelyEqual(payoff.maximum_loss_usd, debit)
      || !approximatelyEqual(payoff.target_profit_usd, targetProfit)
      || !approximatelyEqual(payoff.loss_at_invalidation_usd, invalidationLoss)
      || !approximatelyEqual(payoff.breakeven_underlying, breakeven)
      || (maximumProfit === null) !== payoff.maximum_profit_unlimited
      || (maximumProfit === null
        ? payoff.maximum_profit_usd !== null
        : payoff.maximum_profit_usd === null || !approximatelyEqual(payoff.maximum_profit_usd, maximumProfit))
      || !approximatelyEqual(payoff.limit_entry_debit_usd, limitDebit)
      || !approximatelyEqual(payoff.limit_maximum_loss_usd, limitDebit)
      || !approximatelyEqual(payoff.limit_target_profit_usd, limitTargetProfit)
      || !approximatelyEqual(payoff.limit_invalidation_loss_usd, limitInvalidationLoss)
      || !approximatelyEqual(payoff.limit_breakeven_underlying, limitBreakeven)
      || (limitMaximumProfit === null
        ? payoff.limit_maximum_profit_usd !== null
        : payoff.limit_maximum_profit_usd === null || !approximatelyEqual(payoff.limit_maximum_profit_usd, limitMaximumProfit))
    ) {
      context.addIssue({ code: "custom", message: "Published payoff does not match the exact legs, entry, and setup levels" });
    }
  }
  for (const alternative of row.alternatives) {
    const trade = alternative.exact_trade;
    const payoff = alternative.payoff;
    const bullishStrategy = ["long_call", "bull_call_debit"].includes(trade.strategy_family);
    if (
      bullishStrategy !== (row.direction === "BULLISH")
      || trade.event_authority.status !== "ELIGIBLE"
      || trade.event_authority.complete_through < trade.expiry
      || trade.event_authority.risk_window_policy_version !== "expression_event_risk.through_expiry.v1"
      || Number(payoff.expiration_target_profit_to_max_loss_ratio)
        < Number(trade.minimum_expiration_target_profit_to_max_loss_ratio_required)
      || Number(payoff.maximum_loss_usd) > Number(trade.maximum_loss_usd_allowed)
      || Number(payoff.limit_expiration_target_profit_to_max_loss_ratio)
        < Number(trade.minimum_expiration_target_profit_to_max_loss_ratio_required)
      || Number(payoff.limit_maximum_loss_usd) > Number(trade.maximum_loss_usd_allowed)
    ) {
      context.addIssue({ code: "custom", message: "Ready alternative violates direction, event, or risk authority" });
    }
  }
  if ((row.state === "ENTRY_PENDING") !== (row.pending_reasons.length > 0)) {
    context.addIssue({ code: "custom", message: "Entry pending state requires typed pending reasons and other states forbid them" });
  }
  const monitorableReason = ["ACTIVATION_PENDING", "ENTRY_REEVALUATION"].includes(row.alert_eligibility.reason);
  if ((row.alert_eligibility.status === "MONITORABLE") !== monitorableReason) {
    context.addIssue({ code: "custom", message: "Alert eligibility status and reason are inconsistent" });
  }
});

export const setupDetailSchema = z.object({
  schema_version: z.literal("trade_idea_setup_detail.v5"),
  setup_snapshot_id: setupSnapshotId,
  base_upper: decimalString,
  base_lower: decimalString,
  exact_trade: setupExactTradeSchema.nullable(),
  payoff: setupPayoffSchema.nullable(),
  hard_evidence: z.array(setupWhyNowFactSchema),
  confirmations: z.array(setupWhyNowFactSchema),
  contradictions: z.array(setupWhyNowFactSchema),
  alternatives: z.array(setupReadyAlternativeSchema).max(4),
  primary_expression_id: z.null(),
}).strict();

export const setupPublicationSchema = z.object({
  schema_version: z.literal("trade_idea_setup_publication.v11"),
  policy_version: z.string().min(1),
  cohort_policy_version: z.literal("ready_alternatives.one_per_family.nearest_45d_then_strikes.v1"),
  scope_version: z.literal("trend_breakout.long_options_debit_spreads.v1"),
  scope_label: z.literal("Trend breakout · long options and debit spreads · v1"),
  house_profile_content_hash: z.string().regex(/^ti_house_profile_sha256:[0-9a-f]{64}$/).nullable(),
  internal_expression_selection: internalExpressionSelectionSchema.nullable(),
  internal_alternative: setupReadyAlternativeSchema.nullable(),
  row: setupRowSchema,
  detail: setupDetailSchema,
}).strict().superRefine((publication, context) => {
  if (publication.row.setup_snapshot_id !== publication.detail.setup_snapshot_id) {
    context.addIssue({ code: "custom", message: "Setup row and detail snapshots must match" });
  }
  if (publication.row.state === "READY" && publication.house_profile_content_hash === null) {
    context.addIssue({ code: "custom", message: "Ready setup requires an immutable house-profile binding" });
  }
  if (JSON.stringify(publication.row.exact_trade) !== JSON.stringify(publication.detail.exact_trade)) {
    context.addIssue({ code: "custom", message: "Setup row and detail exact trades must match" });
  }
  if (JSON.stringify(publication.row.payoff) !== JSON.stringify(publication.detail.payoff)) {
    context.addIssue({ code: "custom", message: "Setup row and detail payoff facts must match" });
  }
  if (JSON.stringify(publication.row.alternatives) !== JSON.stringify(publication.detail.alternatives)) {
    context.addIssue({ code: "custom", message: "Setup row and detail alternatives must match" });
  }
  const selection = publication.internal_expression_selection;
  if (selection === null) {
    if (publication.internal_alternative !== null) {
      context.addIssue({ code: "custom", message: "Internal alternative requires selection authority" });
    }
  } else if (selection.state === "SELECTED") {
    if (publication.row.alternatives.length > 0 || publication.row.exact_trade?.candidate_id !== selection.primary_candidate_id) {
      context.addIssue({ code: "custom", message: "Internal selection must match the singular exact trade" });
    }
    if ((publication.internal_alternative?.exact_trade.candidate_id ?? null) !== selection.alternative_candidate_id) {
      context.addIssue({ code: "custom", message: "Internal alternative facts must match selection authority" });
    }
  } else if (publication.row.exact_trade !== null || publication.internal_alternative !== null) {
    context.addIssue({ code: "custom", message: "Internal abstention cannot publish selected trade facts" });
  }
  const lower = numeric(publication.detail.base_lower);
  const upper = numeric(publication.detail.base_upper);
  const trigger = numeric(publication.row.trigger);
  if (
    lower >= upper
    || (publication.row.direction === "BULLISH" ? trigger <= upper : trigger >= lower)
  ) {
    context.addIssue({ code: "custom", message: "Setup trigger does not break the published base in its direction" });
  }
});

export const setupReferenceMintSchema = z.object({
  schema_version: z.literal("trade_idea_setup_reference_mint.v1"),
  trade_ref: tradeReference,
  setup_id: setupId,
  setup_snapshot_id: setupSnapshotId,
  created_at: isoTimestamp,
  expires_at: isoTimestamp,
  copy_prompt: z.string().min(1),
}).strict();

export const setupActivationBoundary = z.string().regex(
  /^UNDERLYING_TRIGGER:(?=.*[1-9])(?:0|[1-9]\d*)(?:\.\d+)?$/,
  "Expected a typed underlying-trigger activation boundary",
);

export function setupActivationTrigger(boundary: string): string {
  return setupActivationBoundary.parse(boundary).slice("UNDERLYING_TRIGGER:".length);
}

export const setupSubscriptionPreviewSchema = z.object({
  operation: z.literal("CREATE_ONE_SHOT_SETUP_SUBSCRIPTION"),
  setup_id: setupId,
  setup_snapshot_id: setupSnapshotId,
  profile_id: z.string().min(1),
  profile_version: z.string().min(1),
  activation_boundary: setupActivationBoundary,
  checked_about_every_seconds: z.number().int().positive(),
  confirmation_token: z.string().min(16),
  confirmation_expires_at: isoTimestamp,
  subscription_expires_at: isoTimestamp,
}).strict();

export const setupSubscriptionSchema = z.object({
  subscription_id: z.string().min(1),
  setup_id: setupId,
  setup_snapshot_id: setupSnapshotId,
  profile_id: z.string().min(1),
  profile_version: z.string().min(1),
  activation_boundary: setupActivationBoundary,
  lifecycle: z.enum(["ACTIVE", "COMPLETED", "CANCELLED", "EXPIRED", "INVALIDATED"]),
  created_at: isoTimestamp,
  expires_at: isoTimestamp,
  next_evaluation_at: isoTimestamp,
  terminal_reason: z.string().nullable().optional(),
}).strict();

export const setupSubscriptionConfirmationSchema = setupSubscriptionSchema;

export const setupSubscriptionListSchema = z.object({
  items: z.array(setupSubscriptionSchema),
}).strict();

export const setupScanCompletenessSchema = z.object({
  schema_version: z.literal("trade_idea_setup_scan_completeness.v1"),
  run_id: z.string().min(1).nullable(),
  status: z.enum(["COMPLETE", "PARTIAL", "RUNNING", "UNAVAILABLE"]),
  scheduled_at: isoTimestamp.nullable(),
  finalized_at: isoTimestamp.nullable(),
  expected_count: z.number().int().nonnegative(),
  completed_count: z.number().int().nonnegative(),
  unavailable_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  missing_count: z.number().int().nonnegative(),
  expected_cell_count: z.number().int().nonnegative(),
  completed_cell_count: z.number().int().nonnegative(),
  unavailable_cell_count: z.number().int().nonnegative(),
  failed_cell_count: z.number().int().nonnegative(),
  missing_cell_count: z.number().int().nonnegative(),
  expected_symbol_count: z.number().int().nonnegative(),
  coherent: z.boolean(),
  reason_codes: z.array(z.string().min(1)),
}).strict().superRefine((scan, context) => {
  const partition = scan.completed_count + scan.unavailable_count + scan.failed_count + scan.missing_count;
  const cellPartition = scan.completed_cell_count
    + scan.unavailable_cell_count
    + scan.failed_cell_count
    + scan.missing_cell_count;
  if (partition !== scan.expected_count) {
    context.addIssue({ code: "custom", message: "Scan counts must exactly partition the expected universe" });
  }
  if (cellPartition !== scan.expected_cell_count) {
    context.addIssue({ code: "custom", message: "Scan cell counts must exactly partition the expected cell universe" });
  }
  if (scan.expected_symbol_count !== scan.expected_count) {
    context.addIssue({ code: "custom", message: "Expected symbol count must match the expected universe" });
  }
  if (scan.status === "COMPLETE" && (
    !scan.coherent
    || scan.run_id === null
    || scan.scheduled_at === null
    || scan.finalized_at === null
    || scan.completed_count !== scan.expected_count
    || scan.unavailable_count !== 0
    || scan.failed_count !== 0
    || scan.missing_count !== 0
  )) context.addIssue({ code: "custom", message: "Complete scan requires a coherent finalized all-success partition" });
  if (scan.status === "RUNNING" && (
    !scan.coherent || scan.run_id === null || scan.scheduled_at === null || scan.finalized_at !== null || scan.missing_count === 0
  )) context.addIssue({ code: "custom", message: "Running scan requires coherent unfinished coverage" });
  if (scan.status === "PARTIAL" && (
    !scan.coherent
    || scan.run_id === null
    || scan.scheduled_at === null
    || scan.finalized_at === null
    || scan.unavailable_count + scan.failed_count + scan.missing_count === 0
  )) context.addIssue({ code: "custom", message: "Partial scan requires coherent finalized non-success coverage" });
  if (scan.status === "UNAVAILABLE" && (scan.coherent || scan.reason_codes.length === 0)) {
    context.addIssue({ code: "custom", message: "Unavailable scan status requires an incoherent authority and reason" });
  }
});

export const setupUniverseAuthoritySchema = z.object({
  universe_version: z.string().min(1),
  discovery_snapshot_id: z.string().regex(/^mdu_sha256:[0-9a-f]{64}$/).nullable(),
  selection_manifest_id: z.string().regex(/^ti_universe_selection_sha256:[0-9a-f]{64}$/).nullable(),
  selection_policy_version: z.string().min(1).nullable(),
  evidence_fingerprint: z.string().regex(/^sha256:[0-9a-f]{64}$/),
}).strict();

export const setupUniverseSummarySchema = z.object({
  schema_version: z.literal("trade_idea_setup_universe_funnel.v1"),
  snapshot_captured_at: isoTimestamp.nullable(),
  fresh_through: isoTimestamp.nullable(),
  freshness: z.enum(["FRESH", "STALE", "UNAVAILABLE"]),
  discovered_count: z.number().int().nonnegative(),
  cheap_screen_eligible_count: z.number().int().nonnegative(),
  cheap_screen_excluded_count: z.number().int().nonnegative(),
  deep_scan_count: z.number().int().nonnegative(),
  evaluated_count: z.number().int().nonnegative(),
  completed_count: z.number().int().nonnegative(),
  watch_count: z.number().int().nonnegative(),
  ready_count: z.number().int().nonnegative(),
}).strict().superRefine((summary, context) => {
  const clocksAreValid = summary.snapshot_captured_at !== null
    && summary.fresh_through !== null
    && new Date(summary.fresh_through) > new Date(summary.snapshot_captured_at);
  if (
    summary.cheap_screen_eligible_count + summary.cheap_screen_excluded_count !== summary.discovered_count
    || summary.deep_scan_count > summary.cheap_screen_eligible_count
    || summary.evaluated_count > summary.deep_scan_count
    || summary.completed_count > summary.evaluated_count
    || summary.watch_count + summary.ready_count > summary.completed_count
    || (summary.freshness === "UNAVAILABLE" ? summary.snapshot_captured_at !== null || summary.fresh_through !== null : !clocksAreValid)
  ) context.addIssue({ code: "custom", message: "Universe funnel counts or freshness are inconsistent" });
});

export const setupUniverseDecisionSchema = z.object({
  ticker: z.string().min(1).max(15),
  outcome: z.enum(["EXCLUDED", "CHEAP_ONLY", "DEEP_SCAN"]),
  reasons: z.array(z.object({
    code: z.string().min(1),
    text: z.string().min(1).max(180),
  }).strict()).min(1),
  evaluation_status: z.enum(["COMPLETED", "UNAVAILABLE", "FAILED", "NOT_EVALUATED"]),
  trade_state: z.enum(["READY", "WATCH"]).nullable(),
}).strict();

export const setupUniverseLedgerSchema = setupUniverseSummarySchema.extend({
  as_of: isoTimestamp,
  decisions: z.array(setupUniverseDecisionSchema).max(100),
  next_cursor: z.string().nullable(),
  authority: setupUniverseAuthoritySchema.nullable(),
}).strict();

export const setupListSchema = z.object({
  schema_version: z.literal("trade_idea_setup_list.v1"),
  as_of: isoTimestamp,
  scan: setupScanCompletenessSchema,
  universe: setupUniverseSummarySchema.nullable().optional(),
  items: z.array(setupPublicationSchema),
  next_cursor: z.string().nullable(),
}).strict();

export type TradeIdeasList = z.infer<typeof tradeIdeasListSchema>;
export type TradeIdeaListItem = z.infer<typeof tradeIdeaListItemSchema>;
export type TradeIdeaDetail = z.infer<typeof tradeIdeaDetailSchema>;
export type TradeIdeaOpportunity = z.infer<typeof opportunityListItemSchema>;
export type TradeIdeaOpportunityDetail = z.infer<typeof opportunityDetailSchema>;
export type TradeIdeaOpportunityList = z.infer<typeof opportunityListSchema>;
export type HouseProfile = z.infer<typeof houseProfileSchema>;
export type TradeReferenceMintResponse = z.infer<typeof tradeReferenceMintResponseSchema>;
export type CompareIdeaItem = z.infer<typeof compareItemSchema>;
export type SubscriptionPreview = z.infer<typeof subscriptionPreviewSchema>;
export type IdeaSubscription = z.infer<typeof ideaSubscriptionSchema>;
export type IdeaSubscriptionList = z.infer<typeof ideaSubscriptionListSchema>;
export type SubscriptionCancelResponse = z.infer<typeof subscriptionCancelResponseSchema>;
export type TradeIdeaSetup = z.infer<typeof setupPublicationSchema>;
export type TradeIdeaSetupRow = z.infer<typeof setupRowSchema>;
export type TradeIdeaSetupList = z.infer<typeof setupListSchema>;
export type SetupUniverseSummary = z.infer<typeof setupUniverseSummarySchema>;
export type SetupUniverseLedger = z.infer<typeof setupUniverseLedgerSchema>;
export type SetupReferenceMint = z.infer<typeof setupReferenceMintSchema>;
export type SetupSubscriptionPreview = z.infer<typeof setupSubscriptionPreviewSchema>;
export type SetupSubscriptionConfirmation = z.infer<typeof setupSubscriptionConfirmationSchema>;
export type SetupSubscription = z.infer<typeof setupSubscriptionSchema>;
export type SetupSubscriptionList = z.infer<typeof setupSubscriptionListSchema>;

export function decodeTradeIdeasList(input: unknown): TradeIdeasList {
  return tradeIdeasListSchema.parse(input);
}

export function decodeTradeIdeaDetail(input: unknown): TradeIdeaDetail {
  return tradeIdeaDetailSchema.parse(input);
}

export function decodeTradeIdeaOpportunities(input: unknown): TradeIdeaOpportunityList {
  return opportunityListSchema.parse(input);
}

export function decodeTradeIdeaOpportunity(input: unknown): TradeIdeaOpportunityDetail {
  return opportunityDetailSchema.parse(input);
}

export function decodeHouseProfile(input: unknown): HouseProfile {
  return houseProfileSchema.parse(input);
}

export function decodeTradeReferenceMintResponse(input: unknown): TradeReferenceMintResponse {
  return tradeReferenceMintResponseSchema.parse(input);
}

export function decodeSubscriptionPreview(input: unknown): SubscriptionPreview {
  return subscriptionPreviewSchema.parse(input);
}

export function decodeIdeaSubscription(input: unknown): IdeaSubscription {
  return ideaSubscriptionSchema.parse(input);
}

export function decodeIdeaSubscriptionList(input: unknown): IdeaSubscriptionList {
  return ideaSubscriptionListSchema.parse(input);
}

export function decodeSubscriptionCancelResponse(input: unknown): SubscriptionCancelResponse {
  return subscriptionCancelResponseSchema.parse(input);
}

export function decodeTradeIdeaSetups(input: unknown): TradeIdeaSetupList {
  return setupListSchema.parse(input);
}

export function decodeSetupUniverseLedger(input: unknown): SetupUniverseLedger {
  return setupUniverseLedgerSchema.parse(input);
}

export function decodeTradeIdeaSetup(input: unknown): TradeIdeaSetup {
  return setupPublicationSchema.parse(input);
}

export function decodeSetupReferenceMint(input: unknown): SetupReferenceMint {
  return setupReferenceMintSchema.parse(input);
}

export function decodeSetupSubscriptionPreview(input: unknown): SetupSubscriptionPreview {
  return setupSubscriptionPreviewSchema.parse(input);
}

export function decodeSetupSubscriptionConfirmation(input: unknown): SetupSubscriptionConfirmation {
  return setupSubscriptionConfirmationSchema.parse(input);
}

export function decodeSetupSubscription(input: unknown): SetupSubscription {
  return setupSubscriptionSchema.parse(input);
}

export function decodeSetupSubscriptionList(input: unknown): SetupSubscriptionList {
  return setupSubscriptionListSchema.parse(input);
}

// --------------------------------------------------------------------------- //
// INTERNAL_SHADOW_RESEARCH multi-strategy screener (barebones research view).  //
// Permissive on purpose: this reads a shadow/internal collection whose shape   //
// may drift; the wireframe should surface rows, not block on strict typing.    //
// --------------------------------------------------------------------------- //

// Strict canonical decimal string for the two JS-1051/1052 enrichment groups
// -- reuses the file's existing `decimalString` shape (no leading zeros, no
// `+`, no hex/scientific notation, no whitespace) so a malformed leaf value
// (numeric-typed, "0x10", "  ", "1e5") fails validation at the boundary
// rather than being silently coerced downstream by `Number()`.
const enrichmentDecimalString = decimalString;

// JS-1051: per-leg + net execution friction (mid / realistic fill /
// spread-width-pct) computed from the entry's own REAL priced legs.
// NON-CANONICAL and absent on entries without real priced legs. Numerics
// arrive as lossless decimal strings (see screener_reads._decimal_to_str).
const multiStrategyExecutionFrictionLegShape = z
  .object({
    contract: z.string().nullish(),
    mid: enrichmentDecimalString.nullish(),
    spread_dollars: enrichmentDecimalString.nullish(),
    spread_pct_of_mid: enrichmentDecimalString.nullish(),
    realistic_fill: enrichmentDecimalString.nullish(),
  })
  .passthrough();

const multiStrategyExecutionFrictionShape = z
  .object({
    schema_version: z.string().nullish(),
    net_mid_per_share: enrichmentDecimalString.nullish(),
    net_realistic_fill_per_share: enrichmentDecimalString.nullish(),
    spread_width_pct_of_premium: enrichmentDecimalString.nullish(),
    // Deliberately NOT `.nullish()`: a truly malformed `legs: null` must fail
    // THIS field's own validation so the group-level `.catch(undefined)`
    // wrapper (applied where this schema is referenced on the entry) drops
    // the whole group -- an array field that silently accepted `null` would
    // parse through with a lie (`legs: null` looks like zero legs, not "we
    // don't actually know the legs").
    legs: z.array(multiStrategyExecutionFrictionLegShape).default([]),
  })
  .passthrough();

// JS-1052: a MODEL-IMPLIED risk-neutral estimate from leg IVs (equal-weight),
// a lognormal terminal distribution, zero rates, and no dividends -- NOT a
// forecast, win-rate claim, or investment advice. `probability_of_profit` is
// a fraction in [0, 1] (multiply by 100 for display). NON-CANONICAL and
// absent on entries without real priced legs or a real per-leg IV.
//
// JS-1112 round 3 finding 2: `max_profit_unbounded`/`max_loss_unbounded` are
// tolerant, optional booleans (`.nullish()`, never required) -- an older
// persisted row, or a reading from before this fix shipped, simply omits
// them, and this schema must not throw the whole entry out over their
// absence. A present boolean is real (`true`/`false`), never a stand-in for
// "unavailable" -- see screenerLabels' rendering helpers for the "uncapped"
// vs "—" distinction this drives.
const multiStrategyImpliedProbabilityReadingShape = z
  .object({
    probability_of_profit: enrichmentDecimalString.nullish(),
    expected_value_per_share: enrichmentDecimalString.nullish(),
    expected_value_usd: enrichmentDecimalString.nullish(),
    max_profit_unbounded: z.boolean().nullish(),
    max_loss_unbounded: z.boolean().nullish(),
  })
  .passthrough();

const multiStrategyImpliedProbabilityShape = z
  .object({
    schema_version: z.string().nullish(),
    method: z.string().nullish(),
    probability_of_profit: enrichmentDecimalString.nullish(),
    expected_value_per_share: enrichmentDecimalString.nullish(),
    expected_value_usd: enrichmentDecimalString.nullish(),
    max_profit_unbounded: z.boolean().nullish(),
    max_loss_unbounded: z.boolean().nullish(),
    at_mid: multiStrategyImpliedProbabilityReadingShape.nullish(),
    structure_type: z.string().nullish(),
    structure_implied_volatility: enrichmentDecimalString.nullish(),
    // JS-1112 round 3 finding 1: caller-side provenance of WHERE the
    // reference IV driving this reading came from -- distinct from
    // `method` (which terminal-distribution MODEL the calculator ran).
    // Tolerant/optional: absent on a row from before this fix shipped.
    reference_iv_source: z.string().nullish(),
    reference_iv_tenor_days: z.number().nullish(),
  })
  .passthrough();

// Enrichment is NON-CANONICAL decoration merged in at read time from a
// SEPARATE document field (see screener_reads._merge_entry_enrichment) -- it
// must NEVER take down the response. Every canonical row/entry field above
// keeps its existing strictness; only these two groups get a `.catch`
// wrapper so a malformed shape (legs: null, a numeric-typed probability, a
// non-object group, garbage decimal strings) degrades to `undefined` for
// JUST that field on JUST that entry, instead of throwing out of `.parse()`
// and replacing every valid row in the response with the table-level error.
export const multiStrategyExecutionFrictionLegSchema = multiStrategyExecutionFrictionLegShape;
export const multiStrategyExecutionFrictionSchema = multiStrategyExecutionFrictionShape;
export const multiStrategyImpliedProbabilityReadingSchema = multiStrategyImpliedProbabilityReadingShape;
export const multiStrategyImpliedProbabilitySchema = multiStrategyImpliedProbabilityShape;

const tolerantExecutionFriction = multiStrategyExecutionFrictionShape.nullish().catch(undefined);
const tolerantImpliedProbability = multiStrategyImpliedProbabilityShape.nullish().catch(undefined);

// JS-1075 (Edge Engine T1a): the DELTA-HEDGED gamma/theta carry threshold --
// the daily |move| at which a delta-hedged book's gamma P&L offsets that
// day's theta decay (the standard vol-desk carry number, NOT the position's
// own unhedged directional breakeven -- see
// domains.trade_ideas.carry_scenario's module docstring), computed from the
// SAME leg_greeks_inputs book_summary already consumes (see
// domains.trade_ideas.carry_scenario.compute_carry_breakeven). NON-CANONICAL
// and absent on entries without real priced legs or a real per-leg IV.
//
// Retro adversarial-review FIX 2 (``carry_breakeven.v2``): `state` is a
// sign-quadrant classification over SIGNED net theta/gamma -- "MOVE_REQUIRED"
// (theta<0, gamma>0, a real required-move threshold), "THETA_COLLECTS"
// (theta>=0 and gamma>=0, OR theta>0 and gamma<0 -- collects today, possibly
// with an `adverse_move_ceiling_usd`), "DECAY_DOMINATES" (theta<0 AND
// gamma<0 -- both greeks work against the position, no finite move offsets
// it), and "GAMMA_FLAT" (gamma==0, theta<0, the degenerate case). Tolerant to
// BOTH the v1 three-state payload (no `DECAY_DOMINATES`, no
// `adverse_move_ceiling_usd`) and the v2 payload: the new state/field are
// simply absent on an older row, never required.
const multiStrategyCarryBreakevenShape = z
  .object({
    schema_version: z.string().nullish(),
    state: z.enum(["MOVE_REQUIRED", "THETA_COLLECTS", "DECAY_DOMINATES", "GAMMA_FLAT"]).nullish(),
    net_theta_dollars_per_day: enrichmentDecimalString.nullish(),
    net_gamma_dollars: enrichmentDecimalString.nullish(),
    required_daily_move_usd: enrichmentDecimalString.nullish(),
    expected_daily_move_usd: enrichmentDecimalString.nullish(),
    required_to_expected_ratio: enrichmentDecimalString.nullish(),
    // Retro FIX 2 (``carry_breakeven.v2``): populated ONLY on THETA_COLLECTS
    // when net gamma is negative -- the daily |move| at which gamma losses
    // consume the day's collected theta. Nullish so an older (v1) payload,
    // or any other state, parses unchanged with this fact honestly absent.
    adverse_move_ceiling_usd: enrichmentDecimalString.nullish(),
  })
  .passthrough();

export const multiStrategyCarryBreakevenSchema = multiStrategyCarryBreakevenShape;
const tolerantCarryBreakeven = multiStrategyCarryBreakevenShape.nullish().catch(undefined);

// JS-1075 (Edge Engine T1a): "what if I'm right on direction but wrong on
// vol/timing?" -- a small spot x vol x time P&L matrix re-priced with the
// same Black-Scholes conventions book_summary uses, valued against the
// structure's own natural (executable) entry fill (see
// domains.trade_ideas.carry_scenario.compute_scenario_grid). NON-CANONICAL
// and absent on entries without real priced legs, a real per-leg IV, or a
// usable two-sided quote for every leg.
// Retro adversarial-review FIX 4 (``scenario_grid.v2``): adds
// "NEAR_EXPIRY_LATEST_VALID" -- the floor slice, when the entry is already
// at/inside the 21-DTE floor and the floor date degraded to the day before
// expiry (see domains.trade_ideas.carry_scenario._time_slices). Tolerant to
// an older (v1) payload that never emits this value.
const multiStrategyScenarioCellShape = z
  .object({
    spot_sigma_multiple: enrichmentDecimalString.nullish(),
    spot: enrichmentDecimalString.nullish(),
    vol_shock: enrichmentDecimalString.nullish(),
    time_slice: z.enum(["HALF_LIFE_TO_DTE_FLOOR", "AT_DTE_FLOOR", "NEAR_EXPIRY_LATEST_VALID"]).nullish(),
    days_to_expiry: enrichmentDecimalString.nullish(),
    structure_value_per_share: enrichmentDecimalString.nullish(),
    pnl_per_share: enrichmentDecimalString.nullish(),
    pnl_usd: enrichmentDecimalString.nullish(),
  })
  .passthrough();

export const multiStrategyScenarioCellSchema = multiStrategyScenarioCellShape;

// Adversarial-review FIX 4: `.max(30)` is defense-in-depth, matching the
// backend's own `MAXIMUM_GRID_CELLS` cap (services/apis/domains/trade_ideas
// /carry_scenario.py) -- a truly oversized `cells` array should never reach
// the client, but if it ever does, this bound fails THIS field's own
// validation so the group-level `.catch(undefined)` wrapper below drops just
// the `scenario_grid` group (never the row/page), the SAME tolerance posture
// as the `cells: null` guard already applies.
const multiStrategyScenarioGridShape = z
  .object({
    schema_version: z.string().nullish(),
    entry_price_per_share: enrichmentDecimalString.nullish(),
    // Deliberately NOT `.nullish()`: matches the `execution_friction.legs`
    // precedent above -- a truly malformed `cells: null` must fail THIS
    // field's own validation so the group-level `.catch(undefined)` wrapper
    // drops the whole group rather than silently reading as "zero cells."
    cells: z.array(multiStrategyScenarioCellShape).max(30).default([]),
    // JS-1075 adversarial-review FIX 2 (dropped-cell honesty): optional/
    // tolerant so an OLDER payload without these fields still parses.
    dropped_cells: z.number().int().nonnegative().nullish(),
    dropped_reason: z.enum(["SHOCKED_SPOT_NON_POSITIVE"]).nullish(),
  })
  .passthrough();

export const multiStrategyScenarioGridSchema = multiStrategyScenarioGridShape;
const tolerantScenarioGrid = multiStrategyScenarioGridShape.nullish().catch(undefined);

// JS-1127 (SIGNAL-006): per-leg delta/gamma/theta/vega plus the structure's
// net delta/gamma/theta/vega at entry, signed by BUY/SELL action and the
// standard 100-share contract multiplier -- computed by the ONE shared
// Black-Scholes kernel (domains.insights.calculators.black_scholes_kernel)
// from the SAME leg_greeks_inputs block book_summary/carry_scenario already
// consume (see domains.trade_ideas.leg_greeks.compute_leg_greeks).
// NON-CANONICAL and absent on entries without real priced legs or a real
// per-leg IV -- the SAME unlock condition as leg_greeks_inputs/
// carry_breakeven/scenario_grid above. Decode-only: no new UI beyond this
// contract in JS-1127's initial cut.
const multiStrategyLegGreeksLegShape = z
  .object({
    contract: z.string().nullish(),
    action: z.enum(["BUY", "SELL"]).nullish(),
    option_type: z.enum(["CALL", "PUT"]).nullish(),
    strike: enrichmentDecimalString.nullish(),
    expiry: z.string().nullish(),
    delta: enrichmentDecimalString.nullish(),
    gamma: enrichmentDecimalString.nullish(),
    theta: enrichmentDecimalString.nullish(),
    vega: enrichmentDecimalString.nullish(),
  })
  .passthrough();

const multiStrategyLegGreeksShape = z
  .object({
    schema_version: z.string().nullish(),
    legs: z.array(multiStrategyLegGreeksLegShape).default([]),
    net_delta: enrichmentDecimalString.nullish(),
    net_gamma: enrichmentDecimalString.nullish(),
    net_theta_per_day: enrichmentDecimalString.nullish(),
    net_vega: enrichmentDecimalString.nullish(),
    greek_unit_basis: z
      .object({
        contract_multiplier: z.number().nullish(),
        delta: z.string().nullish(),
        gamma: z.string().nullish(),
        theta: z.string().nullish(),
        vega: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();

export const multiStrategyLegGreeksSchema = multiStrategyLegGreeksShape;
const tolerantLegGreeks = multiStrategyLegGreeksShape.nullish().catch(undefined);

// JS-1127 (SIGNAL-006): the 1-sigma market-implied expected move over the
// hold horizon (the structure's own expiry, from the EXACT SAME resolved
// reference IV reading PoP's own implied_probability already used -- see
// domains.trade_ideas.leg_greeks.compute_expected_move_vs_breakeven) versus
// the entry's nearest breakeven distance from spot. "Expected move" is a
// MARKET-IMPLIED quantity (ADR-011-safe: never a forecast/prediction).
// NON-CANONICAL and absent unless a genuine vol-surface reference IV
// resolved for this structure's expiry (never a second, independently-
// derived reference IV when PoP itself fell back to a long-leg/blend IV).
const multiStrategyExpectedMoveVsBreakevenShape = z
  .object({
    schema_version: z.string().nullish(),
    reference_iv: enrichmentDecimalString.nullish(),
    reference_iv_source: z.string().nullish(),
    horizon_days: z.number().int().nullish(),
    expected_move_horizon_usd: enrichmentDecimalString.nullish(),
    breakeven: enrichmentDecimalString.nullish(),
    spot: enrichmentDecimalString.nullish(),
    breakeven_distance_usd: enrichmentDecimalString.nullish(),
    breakeven_distance_abs_usd: enrichmentDecimalString.nullish(),
    breakeven_distance_in_expected_move_units: enrichmentDecimalString.nullish(),
  })
  .passthrough();

export const multiStrategyExpectedMoveVsBreakevenSchema = multiStrategyExpectedMoveVsBreakevenShape;
const tolerantExpectedMoveVsBreakeven = multiStrategyExpectedMoveVsBreakevenShape
  .nullish()
  .catch(undefined);

// JS-1053: the mechanical exit rule stamped on every LEDGERED idea at
// generation time (target/stop comparisons + a shared 21-DTE floor), read
// verbatim from `domains.trade_ideas.exit_policy.ExitRule.as_extra_fields()`
// via the SAME entry_enrichment seam as carry_breakeven/scenario_grid above
// (services/apis/domains/trade_ideas/screener_reads.py's
// `_merge_entry_enrichment`, keyed by archetype). NON-CANONICAL and ABSENT on
// any entry the exit-policy stamping never ran over (a shadow/unpriced entry,
// or a row written before this field existed) -- never fabricated.
const multiStrategyExitRuleShape = z
  .object({
    schema_version: z.string().nullish(),
    policy_version: z.string().nullish(),
    rule_kind: z.enum([
      "LONG_OPTION_VALUE_MULTIPLE",
      "DEBIT_VERTICAL_PROFIT_CAPTURE",
      "CREDIT_VERTICAL_BUYBACK",
    ]).nullish(),
    target_kind: z.string().nullish(),
    target_value: enrichmentDecimalString.nullish(),
    stop_kind: z.string().nullish(),
    stop_value: enrichmentDecimalString.nullish(),
    dte_floor: z.number().int().nonnegative().nullish(),
  })
  .passthrough();

export const multiStrategyExitRuleSchema = multiStrategyExitRuleShape;
const tolerantExitRule = multiStrategyExitRuleShape.nullish().catch(undefined);

// JS-1081: one real, fully-identified priced leg (contract/action/
// option_type/strike/expiry), read verbatim from the backend's
// `entry.priced_legs` via the SAME entry_enrichment seam as exit_rule above
// (services/apis/scripts/run_multi_strategy_screen.py's
// `_entry_honesty_fields`). Exists so a "+Watch" button can reconstruct a
// `MultiLegDraft` without re-deriving legs from an OCC-string-only field.
// NON-CANONICAL and ABSENT on any entry without real priced legs -- never
// fabricated.
const multiStrategyPricedLegShape = z
  .object({
    contract: z.string().nullish(),
    action: z.enum(["BUY", "SELL"]).nullish(),
    // Verified against the actual backend payload (services/apis/domains/
    // trade_ideas/identity.py's `OptionRight` enum, which every
    // `PricedEntryLeg.option_type` value derives from): upper-case
    // "CALL"/"PUT", never lower-case.
    option_type: z.enum(["CALL", "PUT"]).nullish(),
    strike: enrichmentDecimalString.nullish(),
    expiry: z.string().nullish(),
    quoted_at: isoTimestamp.nullish().catch(undefined),
  })
  .passthrough();

export const multiStrategyPricedLegSchema = multiStrategyPricedLegShape;
// JS-1081 (adversarial-review FIX 6): the SAME per-field fail-soft boundary
// every other enrichment group on this entry uses (see
// tolerantExecutionFriction/tolerantExitRule/tolerantOiWallProximityByContract
// above) -- a malformed priced_legs array (or a malformed element within
// it) drops to `undefined` for THIS field only, never failing the whole
// entry (and never the row or its siblings). Previously this was
// `z.array(...).default([])`, which has no failure boundary of its own: a
// malformed array element would fail this field's own parse and, absent an
// enclosing `.catch`, propagate up through the entry.
const tolerantPricedLegs = z.array(multiStrategyPricedLegShape).nullish().catch(undefined);

// JS-1077 (T2a): implied event move (front-expiry ATM straddle spanning the
// earnings date, priced at its NATURAL/executable ask+ask fill) vs the
// historical earnings-move distribution (median/quartile |move| over trailing
// quarters), plus the rich/cheap ratio between them -- read verbatim from
// `domains.trade_ideas.event_vol.EventVol.as_extra_fields()` via the SAME
// entry_enrichment seam as carry_breakeven/exit_rule above. NON-CANONICAL and
// present ONLY for a ledgered idea inside the catalyst window (the backend
// gate, `_ticker_in_catalyst_window` in run_multi_strategy_screen.py, never
// even attempts this field outside the window). ADR-011-safe: every field is
// a descriptive fact ("priced X% vs Y% median realized"), never
// "overpriced"/"sell it"/directional advice -- `implied_move`/
// `historical_distribution` are EACH independently nullable (see
// event_vol.py's own honest-partial-absence contract: one half can resolve
// without the other), each carrying its own `*_absent_reason` code.
const multiStrategyImpliedEventMoveShape = z
  .object({
    implied_move_pct: enrichmentDecimalString.nullish(),
    straddle_cost_per_share: enrichmentDecimalString.nullish(),
    call_contract: z.string().nullish(),
    put_contract: z.string().nullish(),
    atm_strike: enrichmentDecimalString.nullish(),
    front_expiry: z.string().nullish(),
    days_to_expiry: z.number().int().nullish(),
  })
  .passthrough();

const multiStrategyHistoricalEarningsMoveDistributionShape = z
  .object({
    sample_size: z.number().int().nonnegative().nullish(),
    median_abs_move_pct: enrichmentDecimalString.nullish(),
    p25_abs_move_pct: enrichmentDecimalString.nullish(),
    p75_abs_move_pct: enrichmentDecimalString.nullish(),
    quarters_requested: z.number().int().nonnegative().nullish(),
  })
  .passthrough();

const multiStrategyEventVolShape = z
  .object({
    schema_version: z.string().nullish(),
    implied_move: multiStrategyImpliedEventMoveShape.nullish(),
    implied_move_absent_reason: z.string().nullish(),
    historical_distribution: multiStrategyHistoricalEarningsMoveDistributionShape.nullish(),
    historical_absent_reason: z.string().nullish(),
    implied_to_historical_ratio: enrichmentDecimalString.nullish(),
  })
  .passthrough();

export const multiStrategyImpliedEventMoveSchema = multiStrategyImpliedEventMoveShape;
export const multiStrategyHistoricalEarningsMoveDistributionSchema =
  multiStrategyHistoricalEarningsMoveDistributionShape;
export const multiStrategyEventVolSchema = multiStrategyEventVolShape;
const tolerantEventVol = multiStrategyEventVolShape.nullish().catch(undefined);

// JS-1077 (T2a, ADDED SCOPE): the event-window x exit-clock fact -- does the
// stamped mechanical exit (`exit_rule.dte_floor`, 21 DTE) fire BEFORE the
// earnings session, or does the position HOLD THROUGH it? Read verbatim from
// `domains.trade_ideas.event_vol.EarningsExitClock.as_extra_fields()`. A
// SIBLING schema stamp to `event_vol` (own `schema_version`,
// `trade_idea_earnings_exit_clock.v1`) -- the two fact groups evolve
// independently, never sharing one version. `outcome` is one of exactly two
// values (never a third): the TIE case (the DTE-floor session equals the
// earnings session) is documented backend-side to resolve to
// HOLDS_THROUGH_EARNINGS, the conservative/honest side -- see
// event_vol.compute_earnings_exit_clock's docstring.
const multiStrategyEarningsExitClockShape = z
  .object({
    schema_version: z.string().nullish(),
    outcome: z.enum(["EXITS_BEFORE_EARNINGS", "HOLDS_THROUGH_EARNINGS"]).nullish(),
    sessions_to_earnings: z.number().int().nullish(),
    dte_floor_session_date: z.string().nullish(),
    earnings_session_date: z.string().nullish(),
    dte_floor_days: z.number().int().nonnegative().nullish(),
  })
  .passthrough();

export const multiStrategyEarningsExitClockSchema = multiStrategyEarningsExitClockShape;
const tolerantEarningsExitClock = multiStrategyEarningsExitClockShape.nullish().catch(undefined);

// JS-1078 (T2b): the top-K OI-concentration strikes at the idea's own
// near-tenor expiry (read verbatim from
// `domains.trade_ideas.oi_walls.OiWalls.as_extra_fields()`) plus, per leg, a
// wall-proximity annotation (`oi_wall_proximity_by_contract`, keyed by the
// leg's OCC contract symbol -- read verbatim from
// `domains.trade_ideas.oi_walls.LegWallProximity.as_extra_fields()`). Rides
// the SAME entry_enrichment seam as carry_breakeven/event_vol above.
// NON-CANONICAL and, unlike JS-1077's event_vol, NOT catalyst-window-gated --
// present on every entry with real priced legs and a real OI reading at its
// tenor (see run_multi_strategy_screen._row_entry_enrichment). ADR-011:
// walls are OI-CONCENTRATION FACTS, never a dealer-direction claim -- every
// field here is a plain number/boolean, never a directional/advisory one
// (see oi_walls.py's module docstring). `oi_walls_absent_reason` (a sibling,
// like `earnings_exit_clock_absent_reason`) carries the kernel's own honest
// reason code (OI_DATA_UNAVAILABLE / CHAIN_TOO_SPARSE / NO_LISTED_EXPIRY /
// OI_MEDIAN_NOT_POSITIVE) when the group itself is absent because the
// kernel tried and abstained, distinct from "no raw_contracts at all"
// (which omits both the group AND the reason).
//
// Adversarial-review FIX 2: `is_largest_wall` marks the strike(s) sharing
// the MAXIMUM OI among this reading's walls -- a renderer uses this to
// reserve "THE largest near-tenor OI shelf" language for the genuine
// maximum, and "an OI shelf" for a lesser (but still threshold-clearing)
// wall (see screenerLabels.oiWallProximityCopy).
const multiStrategyOiWallStrikeShape = z
  .object({
    strike: enrichmentDecimalString.nullish(),
    open_interest: enrichmentDecimalString.nullish(),
    volume: enrichmentDecimalString.nullish(),
    distance_from_spot: enrichmentDecimalString.nullish(),
    is_wall: z.boolean().nullish(),
    is_largest_wall: z.boolean().nullish(),
  })
  .passthrough();

export const multiStrategyOiWallStrikeSchema = multiStrategyOiWallStrikeShape;

// Defense-in-depth cap matching the backend's own TOP_K_STRIKES=5 (see
// oi_walls.py) -- the SAME "malformed/oversized array fails this field's own
// validation, dropping only this group" posture as scenario_grid's
// `cells.max(30)` guard above.
const multiStrategyOiWallsShape = z
  .object({
    schema_version: z.string().nullish(),
    tenor_expiry: z.string().nullish(),
    median_strike_open_interest: enrichmentDecimalString.nullish(),
    strikes: z.array(multiStrategyOiWallStrikeShape).max(5).default([]),
  })
  .passthrough();

export const multiStrategyOiWallsSchema = multiStrategyOiWallsShape;
const tolerantOiWalls = multiStrategyOiWallsShape.nullish().catch(undefined);

// Adversarial-review FIX 3: `distance_in_strikes` is STRICTLY `0` or `1` --
// the backend kernel (`compute_leg_wall_proximity`) never emits any other
// non-null value (see oi_walls.py: "on" the wall is 0, "one listed strike
// away" is 1, anything further is `is_near_wall: false` with this field
// `null`). A `z.number().int()` here would have silently accepted a
// malformed `2`/`7` and let the label layer render a false "one strike
// from" claim for it; the strict literal union instead fails THIS field's
// validation on anything else, and the group-level `.catch(undefined)`
// below drops the WHOLE proximity record for that leg rather than render a
// fabricated adjacency claim from a value the backend was never supposed to
// send.
const multiStrategyOiWallProximityShape = z
  .object({
    schema_version: z.string().nullish(),
    leg_contract: z.string().nullish(),
    leg_strike: enrichmentDecimalString.nullish(),
    is_near_wall: z.boolean().nullish(),
    nearby_wall_strike: enrichmentDecimalString.nullish(),
    nearby_wall_open_interest: enrichmentDecimalString.nullish(),
    nearby_wall_is_largest: z.boolean().nullish(),
    distance_in_strikes: z.union([z.literal(0), z.literal(1)]).nullish(),
  })
  .passthrough();

export const multiStrategyOiWallProximitySchema = multiStrategyOiWallProximityShape;
// A tolerant RECORD keyed by OCC contract symbol -- unlike every other
// enrichment group above (one fixed shape per entry), proximity is PER LEG,
// so the key set varies with the structure's own leg count. `.catch({})`
// matches the group-level tolerance posture: a malformed proximity record
// drops to an empty map rather than failing the whole entry.
const tolerantOiWallProximityByContract = z
  .record(z.string(), multiStrategyOiWallProximityShape)
  .nullish()
  .catch(undefined);

// JS-1079 (T2c): HAR-RV realized-vol forecast vs implied (variance risk
// premium v1) -- read verbatim from
// `domains.trade_ideas.har_rv.HarRvForecast.as_extra_fields()`. Rides the
// SAME entry_enrichment seam as carry_breakeven/event_vol/oi_walls above.
// NON-CANONICAL and, like oi_walls, NOT catalyst-window-gated -- present on
// every entry with real priced legs whenever the backend resolved a cached
// OHLCV year AND an ATM implied vol for the name (see
// run_multi_strategy_screen._row_entry_enrichment). This is a MODEL
// ESTIMATE (ADR-009/011): `method` + `fit_r_squared` + `sample_size` are
// disclosed alongside the forecast so the render layer can state its own
// confidence, never a bare number presented as settled fact. Never a
// probability, never advice, and NEVER "cheap"/"rich"/"overpriced" language
// -- see screenerLabels.harRvCopy's wording guard.
// `har_rv_absent_reason` (a sibling, like `oi_walls_absent_reason`) carries
// the kernel's own honest reason code (RV_HISTORY_INSUFFICIENT /
// MODEL_SINGULAR_SYSTEM / MODEL_OUTPUT_INVALID) when the group itself is
// absent because the kernel tried and abstained, distinct from "no cached
// OHLCV series at all" (which omits both the group AND the reason).
// Adversarial-review FIX 5 (JS-1079): refine this group so SEMANTICALLY
// invalid evidence cannot render, not just structurally malformed evidence.
// The pre-fix shape accepted ANY decimal string / any non-negative integer
// for each field independently -- e.g. a garbled `fit_r_squared` of `"1.4"`
// (outside [0, 1], mathematically impossible for a real R^2), a
// `sample_size` of `0` (below the backend's own documented
// `MINIMUM_USABLE_SESSIONS` floor of 200 -- a value the kernel itself can
// never emit for a REAL fit), a stale/garbled `method` stamp left over from
// a PRIOR version of the backend kernel, or a `variance_risk_premium` that
// does not actually equal `atm_implied_reference - rv_forecast_21d_annualized`
// (the kernel's own documented sign convention) would all pass the pre-fix
// shape and render as if they were a genuine, internally-consistent
// forecast. Every constraint below encodes a fact the har_rv.py kernel
// itself GUARANTEES about its own output (see that module's docstring) --
// this is defense-in-depth against a malformed/stale/tampered document, not
// a new business rule invented here.
//
// `method` is checked against the EXACT current stamp (bumped alongside
// har_rv.py's FIX 1: `har_rv_ols_close_to_close_recursive21.v1`) rather than
// merely "is a string" -- a document carrying the OLD pre-recursion stamp
// (`har_rv_ols_close_to_close.v1`) encodes a DIFFERENT, no-longer-accurate
// meaning for `rv_forecast_21d_annualized` (a flat-held one-step value, not
// a genuine recursive 21-step forecast) and must not be silently rendered
// under the new label.
//
// Any refinement failure below drops the WHOLE `har_rv` GROUP via the
// outer `.nullish().catch(undefined)` -- never the entry/row it rides on
// (see `test_har_rv_semantic_refinement_drops_group_not_row` in
// `contracts.test.ts`).
const _HAR_RV_METHOD_STAMP = "har_rv_ols_close_to_close_recursive21.v1";
const _HAR_RV_VRP_RECONCILIATION_TOLERANCE = 0.0005;

const multiStrategyHarRvShape = z
  .object({
    schema_version: z.literal("har_rv.v1"),
    method: z.literal(_HAR_RV_METHOD_STAMP),
    rv_forecast_21d_annualized: enrichmentDecimalString.refine(
      (value) => Number.isFinite(Number(value)) && Number(value) > 0,
      "rv_forecast_21d_annualized must be a strictly positive finite decimal",
    ),
    atm_implied_reference: enrichmentDecimalString.refine(
      (value) => Number.isFinite(Number(value)) && Number(value) > 0,
      "atm_implied_reference must be a strictly positive finite decimal",
    ),
    variance_risk_premium: enrichmentDecimalString,
    fit_r_squared: enrichmentDecimalString.refine((value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1;
    }, "fit_r_squared must lie in [0, 1]"),
    sample_size: z.number().int().gte(200),
  })
  .passthrough()
  .superRefine((harRv, context) => {
    const implied = Number(harRv.atm_implied_reference);
    const forecast = Number(harRv.rv_forecast_21d_annualized);
    const vrp = Number(harRv.variance_risk_premium);
    if (!Number.isFinite(vrp)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "variance_risk_premium must be a finite decimal",
        path: ["variance_risk_premium"],
      });
      return;
    }
    const expected = implied - forecast;
    if (Math.abs(vrp - expected) > _HAR_RV_VRP_RECONCILIATION_TOLERANCE) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "variance_risk_premium must reconcile with atm_implied_reference - rv_forecast_21d_annualized",
        path: ["variance_risk_premium"],
      });
    }
  });

export const multiStrategyHarRvSchema = multiStrategyHarRvShape;
const tolerantHarRv = multiStrategyHarRvShape.nullish().catch(undefined);

// ---------------------------------------------------------------------------
// JS-1091: DeskIdeaArgumentV1 -- setup thesis PROJECTED (never synthesized
// from option prices / reason codes / SMA direction), expressions[] made
// first-class with a stable expression_id, and a server-side named/versioned
// primary-expression selection policy. See
// services/apis/domains/trade_ideas/desk_idea_argument.py for the backend
// projection this mirrors field-for-field. Every group here is TOLERANT
// (permissive shapes + `.catch(undefined)`/`.nullish()`): a legacy row
// produced before this ticket shipped simply omits these keys entirely, and
// a malformed group must drop only ITSELF, never the whole row.
// ---------------------------------------------------------------------------

// JS-1091 round 3 (FIX 3c): bumped to the v2 prefix alongside the backend's
// EXPRESSION_ID_PREFIX (domains.trade_ideas.desk_idea_argument) -- v1 ids
// (bare "ti_expr_sha256:") are superseded; nothing shipped on them.
const expressionId = z.string().regex(/^ti_expr_sha256_v2:[0-9a-f]{64}$/);

export const setupActivationSchema = z
  .object({
    status: z.enum(["PENDING", "ACTIVE", "CHASED", "INVALIDATED"]).nullish(),
    trigger: enrichmentDecimalString.nullish(),
    chase_boundary: enrichmentDecimalString.nullish(),
    observed_at: z.string().nullish(),
  })
  .passthrough();

export const setupInvalidationSchema = z
  .object({
    level: enrichmentDecimalString.nullish(),
    comparator: z.enum(["LESS_THAN", "GREATER_THAN"]).nullish(),
    provenance: z.string().nullish(),
  })
  .passthrough();

export const setupHorizonBlockSchema = z
  .object({
    kind: z.enum(["TACTICAL", "SWING", "POSITION"]).nullish(),
    expected_resolution_sessions: z.number().int().positive().nullish(),
    expires_after_session: z.string().nullish(),
  })
  .passthrough();

export const setupEvidenceCutSliceSchema = z
  .object({
    name: z.string().nullish(),
    observed_at: z.string().nullish(),
    effective_through: z.string().nullish(),
    source_revision: z.string().nullish(),
  })
  .passthrough();

export const setupEvidenceCutBlockSchema = z
  .object({
    id: z.string().nullish(),
    captured_at: z.string().nullish(),
    slices: z.array(setupEvidenceCutSliceSchema).default([]),
  })
  .passthrough();

// The additive setup-thesis block -- present ONLY when `setup_binding.status`
// is `"BOUND"` (a real, ACTIVE SetupSnapshot bound to this row's listing).
// Q1 + activation/invalidation/horizon read ONLY from this block; it is
// NEVER derived from an entry's own reason codes or economics.
export const deskIdeaSetupSchema = z
  .object({
    setup_id: setupId.nullish(),
    setup_snapshot_id: setupSnapshotId.nullish(),
    activation: setupActivationSchema.nullish(),
    invalidation: setupInvalidationSchema.nullish(),
    horizon: setupHorizonBlockSchema.nullish(),
    why_now: z.array(z.string()).default([]),
    contradictions: z.array(z.string()).default([]),
    evidence_cut: setupEvidenceCutBlockSchema.nullish(),
  })
  .passthrough();
const tolerantDeskIdeaSetup = deskIdeaSetupSchema.nullish().catch(undefined);

// ALWAYS present on a row produced after this ticket shipped: `"BOUND"` (a
// setup block rides alongside it) or `"UNBOUND"` with an honest `reason` --
// the row stays a visible candidate either way, but an UNBOUND row's drawer
// must render its entry-derived facts as explicitly-labeled screen-level
// evidence, NEVER a fabricated four-question setup thesis.
export const setupBindingSchema = z
  .object({
    status: z.enum(["BOUND", "UNBOUND"]).nullish(),
    reason: z.string().nullish(),
  })
  .passthrough();
const tolerantSetupBinding = setupBindingSchema.nullish().catch(undefined);

// NON-CANONICAL best-idea composite (Phase 4 curation).  ``best_idea_score`` is
// the top-level quality score as a lossless string; the nested ``best_idea``
// breakdown carries the three-axis explanation.  Permissive on purpose.
// Moved above `deskIdeaExpressionSchema` / `multiStrategyScreenEntrySchema`
// (JS-1091 round 2, FIX 5) so the per-EXPRESSION `best_idea` /
// `expression_best_idea` fields below can reference this SAME shape
// (`bestIdea(row)` already reads) without a forward-reference.
export const confirmationVoteTallySchema = z
  .object({
    available_count: z.number().int().nonnegative(),
    agree_count: z.number().int().nonnegative(),
    contradict_count: z.number().int().nonnegative(),
  })
  .strict();
const tolerantConfirmationVoteTally = confirmationVoteTallySchema.nullish().catch(undefined);

export const liquidityGateFactsSchema = z
  .object({
    worst_spread: enrichmentDecimalString.nullish(),
    min_oi: z.number().int().nonnegative().nullish(),
    min_volume: z.number().int().nonnegative().nullish(),
    leg_count: z.number().int().nonnegative(),
  })
  .strict();
const tolerantLiquidityGateFacts = liquidityGateFactsSchema.nullish().catch(undefined);

export const multiStrategyBestIdeaAxisSchema = z
  .object({
    name: z.string().nullish(),
    score: z.string().nullish(),
    weight: z.string().nullish(),
    uncertainty: z.string().nullish(),
    reasons: z.array(z.string()).default([]),
    confirmation_vote_tally: tolerantConfirmationVoteTally,
    liquidity_gate: tolerantLiquidityGateFacts,
  })
  .passthrough();

export const multiStrategyBestIdeaSchema = z
  .object({
    included: z.boolean().nullish(),
    confidence: z.string().nullish(),
    quality_score: z.string().nullish(),
    // JS-1114 Wave A: whether this expression's OWN primary/priced-ness
    // cleared the curated hard gate (best_ideas.composite.v4) -- absent on
    // any row produced before this policy version shipped. Decode-only: no
    // UI consumes this yet (Wave A ships the gate itself, not a surfaced
    // badge/label).
    curated_priced: z.boolean().nullish(),
    curated_hold_reason_codes: z.array(z.string()).default([]),
    axes: z.array(multiStrategyBestIdeaAxisSchema).default([]),
    policy_version: z.string().nullish(),
    policy_hash: z.string().nullish(),
  })
  .passthrough();
const tolerantExpressionBestIdea = multiStrategyBestIdeaSchema.nullish().catch(undefined);

// One `expressions[]` member -- see
// domains.trade_ideas.desk_idea_argument.ExpressionProjection.as_dict.
export const deskIdeaExpressionSchema = z
  .object({
    expression_id: expressionId.nullish(),
    archetype: z.string().nullish(),
    direction: z.string().nullish(),
    fit_score: z.string().nullish(),
    customer_eligible: z.boolean().nullish(),
    shadow: z.boolean().nullish(),
    priced: z.boolean().nullish(),
    // JS-1091 round 2 (FIX 5): this expression's OWN composite score
    // breakdown, when the server computed one -- see
    // ExpressionProjection.best_idea's docstring.
    best_idea: tolerantExpressionBestIdea,
  })
  .passthrough();

// JS-1091 round 2 (FIX 7): the `expressions[]` GROUP must fail-soft at the
// group BOUNDARY, not just tolerate individual fields within one already-
// parsed element. Round 1's `z.array(deskIdeaExpressionSchema).default([])`
// has no array-level `.catch()` and no per-element `safeParse` -- ONE
// malformed member (a shape this schema did not anticipate) fails the
// WHOLE array during a top-level `.parse()`, which -- unlike a `.nullish()`
// leaf field elsewhere in this file -- can cascade to dropping the ENTIRE
// ROW when the row itself is parsed with `.parse()` rather than
// `.safeParse()` (see `editionIdeasWithDropsSchema` above for the row-level
// precedent this mirrors one level down: per-ELEMENT `safeParse`, dropped
// members counted, never silently swallowed and never blanking the group).
// A malformed `expressions[]` entirely (not an array at all) degrades to an
// empty list via the `z.array(z.unknown())` catch below, exactly like
// every other malformed-shape absence in this module.
const deskIdeaExpressionsWithDropsSchema = z
  .array(z.unknown())
  .catch([])
  .transform((rawItems) => {
    const expressions: DeskIdeaExpression[] = [];
    let droppedCount = 0;
    for (const raw of rawItems) {
      const result = deskIdeaExpressionSchema.safeParse(raw);
      if (result.success) {
        expressions.push(result.data);
      } else {
        droppedCount += 1;
      }
    }
    return { expressions, droppedCount };
  });

export const multiStrategyScreenEntrySchema = z
  .object({
    archetype: z.string().nullish(),
    authority_label: z.string().nullish(),
    customer_eligible: z.boolean().nullish(),
    shadow: z.boolean().nullish(),
    fit_score: z.string().nullish(),
    reason_codes: z.array(z.string()).default([]),
    entry_type: z.string().nullish(),
    natural_entry_per_share: z.string().nullish(),
    maximum_loss_usd: z.string().nullish(),
    maximum_profit_usd: z.string().nullish(),
    breakevens: z.array(z.string()).default([]),
    abstain_reason_codes: z.array(z.string()).default([]),
    // DESK-009 (JS-1144): the ROW-FACE DISCLOSURES -- what the desk is
    // telling the trader ABOUT a structure it is publishing (the cheap-vol
    // waiver admitted this; the model prices it at a negative EV; a rich
    // surface was escaped on corroboration; the market regime never
    // resolved). Distinct from `abstain_reason_codes`, which mean "this did
    // not price": a disclosure always rides a structure that DID.
    //
    // Rides the entry_enrichment seam (never the frozen canonical `entries`
    // array), so it is merged in by `entries_display` at read time -- see
    // `_row_entry_enrichment` for why. Defaults to [] for any older row that
    // predates the disclosures.
    disclosure_reason_codes: z.array(z.string()).default([]),
    expression_ref: z.string().nullish(),
    strikes: z.array(z.string()).default([]),
    expiry: z.string().nullish(),
    provenance_tags: z.array(z.string()).default([]),
    // JS-1091 round 2 (FIX 5): THIS expression's OWN composite score
    // breakdown -- the SAME shape `multiStrategyBestIdeaSchema` already
    // describes for the row level, but scoped to this one entry. Every
    // expression-specific claim (the drawer's Signal-breakdown panel, the
    // Ask-Claude prompt's "Signal score" fact) reads from HERE, never from
    // the row-level `best_idea` -- see MultiStrategyScreenTable.tsx's
    // `expressionBestIdea` and askClaudePrompt.ts's `signalScoreLabel`.
    // Absent on a legacy row/entry with no independent per-expression score.
    expression_best_idea: tolerantExpressionBestIdea,
    execution_friction: tolerantExecutionFriction,
    implied_probability: tolerantImpliedProbability,
    carry_breakeven: tolerantCarryBreakeven,
    scenario_grid: tolerantScenarioGrid,
    // JS-1127 (SIGNAL-006): per-leg + net Greeks at entry and expected move
    // vs. breakeven -- the SAME entry_enrichment seam as carry_breakeven/
    // scenario_grid above, riding the SAME leg_greeks_inputs unlock
    // condition. See the schema definitions above for the full provenance.
    leg_greeks: tolerantLegGreeks,
    expected_move_vs_breakeven: tolerantExpectedMoveVsBreakeven,
    exit_rule: tolerantExitRule,
    priced_legs: tolerantPricedLegs,
    // JS-1081 (adversarial-review FIX 2): present ONLY when this entry's
    // outcome-ledger write was CONFIRMED this sweep (a real insert, or an
    // idempotent write-once hit against an already-existing row -- both are
    // real store membership) -- see the backend orchestrator's own
    // sequencing comment (scripts/run_multi_strategy_screen.py) for exactly
    // when this gets stamped. NEVER a speculatively-computed id for an
    // entry that was excluded from curation, hard-failed its own liquidity
    // gate, or whose write raised -- this is a membership PROOF, not a
    // guess. Lets a "+Watch" button attach real provenance linking a fired
    // alert back to the idea. `.catch(undefined)` (JS-1081 adversarial-
    // review FIX 6): the SAME per-field fail-soft boundary every other
    // enrichment field here uses -- a malformed value drops to `undefined`
    // for this field alone, never failing the whole entry.
    ledger_id: z.string().nullish().catch(undefined),
    // JS-1091: the stable, listing-scoped per-entry identity the server
    // stamps -- see domains.trade_ideas.desk_idea_argument.expression_id_for_entry.
    // New enrichment keying uses this; the existing archetype-keyed
    // enrichment above stays for back-compat. `.catch(undefined)`: a
    // malformed value drops for this field alone, matching every sibling
    // field's tolerance.
    //
    // JS-1091 round 2 (FIX 2, stamp authority): merged onto the entry at
    // READ time from the row's separate `expressions` container
    // (services/apis/domains/trade_ideas/screener_reads.py's
    // `_merge_expression_ids`, the SAME archetype-keyed join
    // `entry_enrichment` already uses). `displayEntryForRow` treats a match
    // against the row's `primary_expression_id` as AUTHORITATIVE -- see
    // that function's own docstring in screenerLabels.ts.
    expression_id: z.string().nullish().catch(undefined),
    event_vol: tolerantEventVol,
    earnings_exit_clock: tolerantEarningsExitClock,
    // Adversarial-review FIX 6: an IN-WINDOW row whose exit-clock computation
    // failed (a malformed/expired expiry, or a calendar outage) persists this
    // explicit reason instead of silently omitting the group -- which would
    // be indistinguishable from an out-of-window row (never attempted at
    // all).  Tolerant like every other sibling field here: a malformed value
    // simply drops (``.catch(undefined)``), never the whole entry.
    earnings_exit_clock_absent_reason: z.string().nullish().catch(undefined),
    oi_walls: tolerantOiWalls,
    oi_wall_proximity_by_contract: tolerantOiWallProximityByContract,
    // JS-1078: the sibling absence reason to `earnings_exit_clock_absent_reason`
    // above -- populated ONLY when the backend kernel itself tried and
    // honestly abstained (see oi_walls.py's reason codes), never when
    // oi_walls was never attempted at all (out-of-scope raw_contracts).
    oi_walls_absent_reason: z.string().nullish().catch(undefined),
    har_rv: tolerantHarRv,
    // JS-1079: the sibling absence reason to `oi_walls_absent_reason` above
    // -- populated ONLY when the backend kernel itself tried and honestly
    // abstained (see har_rv.py's reason codes), never when har_rv was never
    // attempted at all (no cached OHLCV series / no atm30_iv available).
    har_rv_absent_reason: z.string().nullish().catch(undefined),
    // JS-1088 (round 2, adversarial-review FIX 1): topology-aware breakeven-
    // distance-as-a-multiple-of-ADR%, wiring the JS-1086 hi-fi Desk spec's
    // already-built (previously parked) screenerLabels.beAdrSignifier /
    // cushionAdrSignifier to a real computed value. Read verbatim from
    // `domains.trade_ideas.be_adr.BeAdrRatio.as_extra_fields()` via the SAME
    // entry_enrichment seam as har_rv above -- a small ENVELOPE (not a bare
    // scalar, unlike round 1) since a renderer must know WHICH payoff class
    // produced this ratio (DEBIT_DIRECTIONAL required-move vs.
    // ONE_SIDED_CREDIT cushion -- opposite threshold directions) before it
    // can choose the right word register/threshold direction; see
    // screenerLabels.beAdrRowView, the ONE place that dispatch happens.
    // NON-CANONICAL and ABSENT on any entry with no resolvable breakeven at
    // all, or an unrecognized archetype (the computation never even runs),
    // or present with the sibling `be_adr_absent_reason` below when a
    // breakeven distance DID resolve but the ADR% side could not (no cached
    // series / a stale one / a degenerate zero-range series) OR the
    // archetype's breakeven COUNT did not match its expected class
    // (TOPOLOGY_MISMATCH).
    be_adr_ratio: z
      .object({
        schema_version: z.string().nullish(),
        method: z.string().nullish(),
        payoff_class: z.enum(["DEBIT_DIRECTIONAL", "ONE_SIDED_CREDIT", "TWO_BREAKEVEN"]).nullish(),
        ratio: enrichmentDecimalString.nullish(),
        breakeven_excess_pct: enrichmentDecimalString.nullish(),
        adr_pct: enrichmentDecimalString.nullish(),
        // JS-1088 (round 2 FIX 1): present ONLY on a condor's BINDING wing
        // (the smaller, more dangerous of the two) -- absent (never `false`)
        // on a standalone one-sided credit or a condor's non-binding wing.
        is_binding_wing: z.boolean().nullish(),
      })
      .passthrough()
      .nullish()
      .catch(undefined),
    // JS-1088: the sibling absence reason to `har_rv_absent_reason` above --
    // populated ONLY when a breakeven distance resolved but the ADR% side
    // could not, OR the archetype's breakeven count did not match its
    // expected payoff class (TOPOLOGY_MISMATCH) -- never when be_adr_ratio
    // was never attempted at all (no resolvable breakeven / unrecognized
    // archetype on this entry).
    be_adr_absent_reason: z.string().nullish().catch(undefined),
    // JS-1088 (round 2 FIX 1): present ONLY for a TWO_BREAKEVEN (condor)
    // entry whose binding wing resolved -- carries BOTH wings' own
    // be_adr_ratio-shaped envelopes (each independently nullable: one wing
    // CAN resolve while the other does not) so the drawer's Q2 can show the
    // full picture, not just the row's single binding-wing summary.
    be_adr_condor_wings: z
      .object({
        schema_version: z.string().nullish(),
        put_side: z
          .object({
            schema_version: z.string().nullish(),
            method: z.string().nullish(),
            payoff_class: z.enum(["DEBIT_DIRECTIONAL", "ONE_SIDED_CREDIT", "TWO_BREAKEVEN"]).nullish(),
            ratio: enrichmentDecimalString.nullish(),
            breakeven_excess_pct: enrichmentDecimalString.nullish(),
            adr_pct: enrichmentDecimalString.nullish(),
            is_binding_wing: z.boolean().nullish(),
          })
          .passthrough()
          .nullish(),
        call_side: z
          .object({
            schema_version: z.string().nullish(),
            method: z.string().nullish(),
            payoff_class: z.enum(["DEBIT_DIRECTIONAL", "ONE_SIDED_CREDIT", "TWO_BREAKEVEN"]).nullish(),
            ratio: enrichmentDecimalString.nullish(),
            breakeven_excess_pct: enrichmentDecimalString.nullish(),
            adr_pct: enrichmentDecimalString.nullish(),
            is_binding_wing: z.boolean().nullish(),
          })
          .passthrough()
          .nullish(),
      })
      .passthrough()
      .nullish()
      .catch(undefined),
  })
  .passthrough();

// JS-1066: the JS-1065 ENERGY-lane per-name composite score, as persisted by
// `EnergyScore.canonical_payload()` (services/apis/domains/trade_ideas
// /energy_signals.py) via the row's non-canonical extra_fields seam
// (run_multi_strategy_screen.py). `composite` and each `signals.*.score` are
// a RATING in [0, 1], NEVER a probability/forecast (mirrors the Signal
// meter's own framing) -- rendered as a 0-100 number with no "%". The three
// cheap-pass-dormant sub-signals (flow_zscore / volume_oi_ratio / iv_extreme)
// simply never carry a non-null score; this schema does not special-case
// them. Permissive on purpose (`.passthrough()` + tolerant leaves): a
// malformed snapshot must drop the BADGE, never the row -- see the
// `.catch(undefined)` wrapper below where this is referenced.
const energyScoreSignalResultShape = z
  .object({
    score: enrichmentDecimalString.nullish(),
    reason_codes: z.array(z.string()).default([]),
  })
  .passthrough();

export const energyScoreSnapshotSchema = z
  .object({
    schema: z.string().nullish(),
    policy_version: z.string().nullish(),
    composite: enrichmentDecimalString.nullish(),
    composite_reason_codes: z.array(z.string()).default([]),
    signals_available: z.number().int().min(0).max(6).nullish(),
    signals: z.record(z.string(), energyScoreSignalResultShape).default({}),
  })
  .passthrough();

const tolerantEnergyScoreSnapshot = energyScoreSnapshotSchema.nullish().catch(undefined);

// JS-1065/1066: which discovery lane surfaced this row -- "ENERGY" or
// "CORE_LIQUID" today, but kept as a permissive string (not an enum) so a
// future lane name never fails validation; absent entirely on a row from a
// run not driven by the two-lane sweep (see energy_lane.py). Tolerant: a
// malformed value drops the badge, never the row.
const tolerantDiscoveryLane = z.string().nullish().catch(undefined);

// JS-1076 (T1b, adversarial-review fix): the RISK-axis macro/Fed-event
// context (services/apis/domains/trade_ideas/best_ideas_score.py's
// `MacroEventContext.as_dict()`), persisted as a MARKET-WIDE top-level field
// by run_multi_strategy_screen.py -- the SAME snapshot on every row of one
// sweep (see that script's `extra_fields["macro_event_context"]` comment).
// `nearest_days_until` is a genuine CALENDAR-day count
// ((event_date - today).days from the FRED tool -- see
// EconomicCalendar.get_economic_calendar), not a trading-session count, so
// the masthead line below reads "days", matching that honest unit.
// Permissive on purpose (all `.nullish()` + group-level `.catch(undefined)`,
// mirroring `tolerantEnergyScoreSnapshot`): a malformed snapshot must drop
// the masthead line, never the row.
const macroEventContextShape = z
  .object({
    available: z.boolean().nullish(),
    nearest_release_name: z.string().nullish(),
    nearest_days_until: z.number().int().nullish(),
    as_of: z.string().nullish(),
    reason_codes: z.array(z.string()).default([]),
  })
  .passthrough();

export const macroEventContextSchema = macroEventContextShape;
const tolerantMacroEventContext = macroEventContextShape.nullish().catch(undefined);

// JS-1068 (adversarial-review FIX 7): the market-wide V4.2 trend-regime
// snapshot (services/apis/domains/trade_ideas/market_regime.py's
// `MarketRegimeSnapshot.canonical_payload()`), threaded through
// `edition.compute_regime_summary`'s additive `market_regime` key -- see
// `editionRegimeSummarySchema` below.
//
// PROVENANCE-REQUIRED, not permissive-on-everything: a masthead line reading
// "Market: BULL" is a claim about a SPECIFIC signal index (QQQ, or the
// documented SPY fallback) as of a SPECIFIC observation session -- rendering
// it from a snapshot missing that provenance (or carrying an unrecognized
// label the backend lattice never actually produces) would silently assert
// a fact the payload cannot support. `label` is therefore a STRICT enum of
// the eight real lattice states (never a bare string -- an unrecognized
// value, e.g. a future/typo'd label, fails the WHOLE snapshot rather than
// rendering a nonsense masthead line), and `schema` / `as_of` /
// `source_ticker` are ALL MANDATORY (non-nullish): every one of these four
// fields must be present and valid for the group to render AT ALL. A
// malformed/incomplete snapshot fails `marketRegimeSnapshotShape` itself,
// which the group-level `.nullish().catch(undefined)` wrapper below then
// turns into "drop the masthead clause" -- never a partial/misleading
// render, and never the whole row/edition.
export const MARKET_REGIME_LABELS = [
  "STRONG_BULL",
  "BULL",
  "TRANSITION",
  "BEAR_WARNING",
  "CONFIRMED_BEAR",
  "PERSISTENT_BEAR",
  "RECOVERY_ABOVE_EMA20",
  "RECOVERY_ABOVE_EMA50",
] as const;

const marketRegimeSnapshotShape = z
  .object({
    schema: z.string().min(1),
    policy_version: z.string().nullish(),
    as_of: z.string().min(1),
    source_ticker: z.string().min(1),
    label: z.enum(MARKET_REGIME_LABELS),
    close: enrichmentDecimalString.nullish(),
    ema20: enrichmentDecimalString.nullish(),
    ema50: enrichmentDecimalString.nullish(),
    sma175: enrichmentDecimalString.nullish(),
    sma200: enrichmentDecimalString.nullish(),
    ema50_rising: z.boolean().nullish(),
    ema50_falling: z.boolean().nullish(),
    sma175_falling: z.boolean().nullish(),
    bear_warning_persistence: z.number().int().nonnegative().nullish(),
    recovery_above_ema20_streak: z.number().int().nonnegative().nullish(),
    recovery_above_ema50_streak: z.number().int().nonnegative().nullish(),
    reason_codes: z.array(z.string()).default([]),
  })
  .passthrough();

export const marketRegimeSnapshotSchema = marketRegimeSnapshotShape;
const tolerantMarketRegimeSnapshot = marketRegimeSnapshotShape.nullish().catch(undefined);

// JS-1102: additive row-level reads. Both groups use the same tolerant
// group-catch boundary as the entry enrichments above: a malformed new group
// disappears by itself and can never make a legacy/otherwise-valid row fail.
// The producer always writes `relative_volume` as a value-or-absence envelope;
// old rows may omit it entirely. IV/RV values are the exact classifier inputs,
// with independent null + reason fields so one missing value never implies the
// other was also unavailable.
export const relativeVolumeReadSchema = z
  .object({
    schema_version: z.literal("relative_volume.v1"),
    available: z.boolean(),
    multiple: enrichmentDecimalString.nullish().catch(undefined),
    lookback_sessions: z.number().int().positive(),
    as_of_session: z.string().date().nullish().catch(undefined),
    reason: z.string().min(1).nullish().catch(undefined),
  })
  .passthrough()
  .superRefine((read, context) => {
    if (read.available && (read.multiple == null || read.as_of_session == null)) {
      context.addIssue({ code: "custom", message: "available relative volume requires multiple and as_of_session" });
    }
    if (!read.available && read.reason == null) {
      context.addIssue({ code: "custom", message: "unavailable relative volume requires a reason" });
    }
  });
const tolerantRelativeVolumeRead = relativeVolumeReadSchema.nullish().catch(undefined);

export const ivRvNumericReadSchema = z
  .object({
    schema_version: z.literal("iv_rv_read.v1"),
    atm30_iv: enrichmentDecimalString.nullish().catch(undefined),
    atm30_iv_absent_reason: z.string().min(1).nullish().catch(undefined),
    realized_vol_20d: enrichmentDecimalString.nullish().catch(undefined),
    realized_vol_20d_absent_reason: z.string().min(1).nullish().catch(undefined),
    term_slope_60d_minus_30d: enrichmentDecimalString.nullish().catch(undefined),
    term_slope_60d_minus_30d_absent_reason: z.string().min(1).nullish().catch(undefined),
    risk_reversal_30d_25delta: enrichmentDecimalString.nullish().catch(undefined),
    risk_reversal_30d_25delta_absent_reason: z.string().min(1).nullish().catch(undefined),
    iv_rv20_spread: enrichmentDecimalString.nullish().catch(undefined),
    iv_rv20_spread_absent_reason: z.string().min(1).nullish().catch(undefined),
    expected_move_30d_ratio: enrichmentDecimalString.nullish().catch(undefined),
    expected_move_30d_ratio_absent_reason: z.string().min(1).nullish().catch(undefined),
    expected_move_30d_usd: enrichmentDecimalString.nullish().catch(undefined),
    expected_move_30d_usd_absent_reason: z.string().min(1).nullish().catch(undefined),
  })
  .passthrough()
  .superRefine((read, context) => {
    if (read.atm30_iv == null && read.atm30_iv_absent_reason == null) {
      context.addIssue({ code: "custom", message: "absent atm30_iv requires a reason" });
    }
    if (read.realized_vol_20d == null && read.realized_vol_20d_absent_reason == null) {
      context.addIssue({ code: "custom", message: "absent realized_vol_20d requires a reason" });
    }
  });
const tolerantIvRvNumericRead = ivRvNumericReadSchema.nullish().catch(undefined);

export const rejectedStrategyCandidateSchema = z
  .object({
    archetype: z.string().min(1),
    fit_score: enrichmentDecimalString,
    rejection_reason_code: z.string().min(1),
    // DESK-009 (JS-1144 ruling #6): the LESSER objection, present on every
    // candidate the backend emits and null whenever the rejection had only
    // one reason.
    //
    // Codex adversarial-review HIGH (round 2): this object is `.strict()`
    // and the array is `.catch(undefined)`, so the moment the backend began
    // emitting this fourth key EVERY rejected-strategy diagnostic silently
    // vanished from the Desk -- not just the new field, the whole group,
    // including the alternative-strategy diagnostics that predate this
    // ticket. Declared nullish (never required) so an older persisted row
    // that lacks the key decodes exactly as before.
    secondary_rejection_reason_code: z.string().min(1).nullish(),
  })
  .strict();
const tolerantRejectedStrategyCandidates = z
  .array(rejectedStrategyCandidateSchema)
  .max(7)
  .nullish()
  .catch(undefined);

export const multiStrategyScreenRowSchema = z
  .object({
    row_id: z.string().nullish(),
    ticker: z.string().nullish(),
    direction: z.string().nullish(),
    iv_rv_regime: z.string().nullish(),
    iv_rank_band: z.string().nullish(),
    iv_rank: z.string().nullish(),
    // JS-1080 (T3a) adversarial-review FIX 7: iv_percentile rides the same
    // seam as iv_rank -- present (a Decimal string) once a name's accrued
    // IV history clears the 60-session floor, explicit null when a store
    // was consulted but the reading is honestly absent, and simply missing
    // for any row produced before this field existed or with no
    // iv_history_store configured for that run.
    iv_percentile: z.string().nullish(),
    // The reason iv_rank/iv_percentile stayed null this run (e.g.
    // IV_HISTORY_INSUFFICIENT, IV_RANGE_DEGENERATE,
    // IV_HISTORY_STORE_UNAVAILABLE, IV_HISTORY_SESSION_NOT_TRADING) --
    // absent whenever a real rank WAS computed.
    iv_rank_absent_reason: z.string().nullish(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    best_idea_score: z.string().nullish(),
    best_idea: multiStrategyBestIdeaSchema.nullish(),
    entries: z.array(multiStrategyScreenEntrySchema).default([]),
    relative_strength: z.record(z.string(), z.unknown()).nullish(),
    options_flow: z.record(z.string(), z.unknown()).nullish(),
    earnings: z.record(z.string(), z.unknown()).nullish(),
    discovery_lane: tolerantDiscoveryLane,
    energy_score_snapshot: tolerantEnergyScoreSnapshot,
    macro_event_context: tolerantMacroEventContext,
    market_regime_at_publication: tolerantMarketRegimeSnapshot,
    // JS-1068 round 3 (adversarial-review FIX 3): the resolver's OWN
    // expected-session anchor for THIS sweep -- a plain ISO date (never a
    // wall-clock timestamp), threaded so the masthead can judge freshness
    // by comparing calendar SESSIONS rather than re-deriving an exchange
    // calendar client-side or misapplying a timestamp TTL to a date field
    // (see screenerLabels.ts's sweepMarketRegime for the frontend half).
    market_regime_expected_session: z.string().nullish(),
    relative_volume: tolerantRelativeVolumeRead,
    iv_rv_read: tolerantIvRvNumericRead,
    rejected_strategy_candidates: tolerantRejectedStrategyCandidates,
    // JS-1091: DeskIdeaArgumentV1's additive fields -- see the group comment
    // above deskIdeaSetupSchema. `setup` rides only when `setup_binding` is
    // BOUND; `setup_binding` itself, `expressions`, and
    // `primary_expression_id`/`primary_expression_selection_policy_version`
    // are computed for every row produced after this ticket shipped and are
    // simply absent on any earlier row.
    setup: tolerantDeskIdeaSetup,
    setup_binding: tolerantSetupBinding,
    // JS-1091 round 2 (FIX 7): fails soft at the GROUP boundary -- see
    // deskIdeaExpressionsWithDropsSchema's own docstring. Flattened back to
    // a plain `expressions` array + a sibling `expressions_dropped_count`
    // by the `.transform()` below, mirroring `editionDetailSchema`'s
    // `ideas`/`ideas_dropped_count` precedent one level up.
    expressions: deskIdeaExpressionsWithDropsSchema,
    primary_expression_id: z.string().nullish().catch(undefined),
    primary_expression_selection_policy_version: z.string().nullish().catch(undefined),
    // JS-1149 (DESK-012): the desk rules in force when this row was decided.
    //
    // NESTED inside `desk008_gate_evidence`, which is where the producer
    // actually writes them (see run_multi_strategy_screen's per-row evidence
    // block) and which the API preserves verbatim through its `extra="allow"`
    // passthrough. Declaring them at the row top level instead -- the first
    // cut of this schema -- meant the panel never saw a knob on real API data
    // and silently rendered nothing (Codex adversarial-review MEDIUM, round
    // 1). The schema now matches the wire shape rather than a hoped-for one.
    //
    // `.passthrough()` + `.catch(undefined)`, matching the tolerant reads
    // above: a malformed rules block must degrade the PANEL to its absent
    // state, never throw out of `.parse()` and blank the whole screener.
    // Absent on any row produced before this ticket -- itself honest: such a
    // row cannot say which knob values judged it.
    desk008_gate_evidence: z
      .object({
        desk_rules_hash: z.string().nullish(),
        desk_rules_version: z.string().nullish(),
        desk_rules_source: z.string().nullish(),
        desk_rules_knobs: z
          .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
          .nullish(),
      })
      .passthrough()
      .nullish()
      .catch(undefined),
  })
  .passthrough()
  .transform((row) => ({
    ...row,
    expressions: row.expressions.expressions,
    expressions_dropped_count: row.expressions.droppedCount,
  }));

// Adversarial-review BLOCKER 1/2 (originally the edition read): the
// backend's own documented fallback (e.g. edition._edition_detail's
// `document.get("book_summary") or {}`) can legitimately emit a bare `{}` in
// place of a real book summary -- honest absence, not malformed input.
// Preprocess `{}` (zero own keys) to `undefined` so it resolves through the
// SAME `.nullish()` path a genuine `null`/absent field already takes,
// letting a BookStrip's existing `if (!book)` empty-state branch actually
// run instead of throwing out of `.parse()` and blanking the whole page.
// Shared by BOTH read surfaces: the JS-1058 screener list's own
// `book_summary` (services/apis/domains/trade_ideas/screener_reads.py's
// `ScreenerReadService.list_screen`, computed over the SAME curated rows
// already returned) and the edition detail's snapshot copy below -- both
// project the exact same `domains.trade_ideas.book_summary.BookSummary.as_dict()`
// shape, so this is ONE schema, not two independently-drifting copies.
function emptyObjectToUndefined(value: unknown): unknown {
  if (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return undefined;
  }
  return value;
}

// book_summary: reuses domains.trade_ideas.book_summary.BookSummary.as_dict().
// `greeks_coverage` arrives pre-formatted "N/M" (see BookSummary.as_dict) --
// rendered verbatim, never re-derived client-side (single source of truth for
// the honest-coverage framing).
const multiStrategyGroupRollupShape = z
  .object({
    key: z.string(),
    idea_count: z.number().int().nonnegative(),
    net_delta_shares: decimalString.nullish(),
    net_vega_dollars: decimalString.nullish(),
    net_theta_dollars_per_day: decimalString.nullish(),
  })
  .passthrough();

const multiStrategyClusterFlagShape = z
  .object({
    axis: z.string(),
    key: z.string(),
    idea_count: z.number().int().nonnegative(),
    tickers: z.array(z.string()).default([]),
    label: z.string(),
  })
  .passthrough();

export const multiStrategyBookSummarySchema = z
  .object({
    schema_version: z.string().nullish(),
    position_assumption: z.string().nullish(),
    shares_per_contract: decimalString.nullish(),
    curated_idea_count: z.number().int().nonnegative(),
    greeks_coverage: z.string(),
    net_delta_shares: decimalString.nullish(),
    net_vega_dollars: decimalString.nullish(),
    net_theta_dollars_per_day: decimalString.nullish(),
    by_underlying: z.array(multiStrategyGroupRollupShape).default([]),
    by_sector: z
      .object({
        available: z.boolean(),
        unavailable_reason: z.string().nullable(),
        rollup: z.array(multiStrategyGroupRollupShape).default([]),
      })
      .passthrough()
      .nullish(),
    cluster_min_names: z.number().int().nonnegative().nullish(),
    cluster_flags: z.array(multiStrategyClusterFlagShape).default([]),
  })
  .passthrough();

// Back-compat alias: the edition module below originally declared this
// schema under its own name before it was hoisted here to be shared with the
// screener list (JS-1076). Same schema object, not a re-derivation.
export const editionBookSummarySchema = multiStrategyBookSummarySchema;

// JS-1094 (demo blocker FIX 6): per-row tolerant -- mirrors
// editionIdeasWithDropsSchema/ledgerRowsWithDropsSchema's identical pattern
// (see those for the full "why"). ONE malformed row (a shape this schema
// didn't anticipate) must never throw out of `.parse()` and blank the WHOLE
// live screener -- before this fix, `multiStrategyScreenListSchema` called
// `z.array(multiStrategyScreenRowSchema)` directly, so `.parse()`'d a single
// corrupt row into a thrown error that replaced the entire screen with the
// table's load-error state, even though every OTHER row in the response was
// perfectly valid. A row that fails `multiStrategyScreenRowSchema` is
// dropped, never silently -- the dropped count rides alongside the
// surviving rows so the table can render an honest "N idea(s) could not be
// displayed" note instead of a blank screen.
const multiStrategyScreenItemsWithDropsSchema = z
  .array(z.unknown())
  .default([])
  .transform((rawItems) => {
    const items: MultiStrategyScreenRow[] = [];
    let droppedCount = 0;
    for (const raw of rawItems) {
      const result = multiStrategyScreenRowSchema.safeParse(raw);
      if (result.success) {
        items.push(result.data);
      } else {
        droppedCount += 1;
      }
    }
    return { items, droppedCount };
  });

export const multiStrategyScreenListSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    count: z.number(),
    // Echoes whether the page was quality-bar curated (best only) or the full
    // score-sorted list.  Optional: older API builds omit it.
    curated: z.boolean().nullish(),
    items: multiStrategyScreenItemsWithDropsSchema,
    // JS-1058: book-level net greeks + concentration flags over `items`,
    // computed PURELY from the rows already returned (see
    // services/apis/domains/trade_ideas/screener_reads.py's
    // ScreenerReadService.list_screen -> compute_book_summary). Same honest
    // `{}` -> undefined fallback as the edition's own book_summary.
    //
    // Retro adversarial-review FIX 6: unlike the edition-detail read below
    // (which deliberately still throws on a genuinely malformed, non-empty
    // book_summary -- see decodeEditionDetail's own tests), the SCREEN-LIST
    // read must fail SOFT here: a partially malformed summary (e.g.
    // `by_underlying: null`, which fails that field's own array schema) must
    // drop only this strip, never the whole screen. `.catch(undefined)`
    // mirrors the SAME per-group tolerance posture every other enrichment
    // group on this list already gets (execution_friction,
    // carry_breakeven, scenario_grid, ...) -- book_summary was the one
    // holdout that could still take the whole page down with it.
    book_summary: z
      .preprocess(emptyObjectToUndefined, multiStrategyBookSummarySchema.nullish())
      .catch(undefined),
  })
  .passthrough()
  .transform((list) => ({
    ...list,
    items: list.items.items,
    items_dropped_count: list.items.droppedCount,
  }));

export type MultiStrategyExecutionFrictionLeg = z.infer<typeof multiStrategyExecutionFrictionLegSchema>;
export type MultiStrategyExecutionFriction = z.infer<typeof multiStrategyExecutionFrictionSchema>;
export type MultiStrategyImpliedProbabilityReading = z.infer<typeof multiStrategyImpliedProbabilityReadingSchema>;
export type MultiStrategyImpliedProbability = z.infer<typeof multiStrategyImpliedProbabilitySchema>;
export type MultiStrategyCarryBreakeven = z.infer<typeof multiStrategyCarryBreakevenSchema>;
export type MultiStrategyScenarioCell = z.infer<typeof multiStrategyScenarioCellSchema>;
export type MultiStrategyScenarioGrid = z.infer<typeof multiStrategyScenarioGridSchema>;
export type MultiStrategyExitRule = z.infer<typeof multiStrategyExitRuleSchema>;
export type MultiStrategyPricedLeg = z.infer<typeof multiStrategyPricedLegSchema>;
export type MultiStrategyImpliedEventMove = z.infer<typeof multiStrategyImpliedEventMoveSchema>;
export type MultiStrategyHistoricalEarningsMoveDistribution = z.infer<
  typeof multiStrategyHistoricalEarningsMoveDistributionSchema
>;
export type MultiStrategyEventVol = z.infer<typeof multiStrategyEventVolSchema>;
export type MultiStrategyEarningsExitClock = z.infer<typeof multiStrategyEarningsExitClockSchema>;
export type MultiStrategyOiWallStrike = z.infer<typeof multiStrategyOiWallStrikeSchema>;
export type MultiStrategyOiWalls = z.infer<typeof multiStrategyOiWallsSchema>;
export type MultiStrategyOiWallProximity = z.infer<typeof multiStrategyOiWallProximitySchema>;
export type EnergyScoreSnapshot = z.infer<typeof energyScoreSnapshotSchema>;
export type MultiStrategyScreenEntry = z.infer<typeof multiStrategyScreenEntrySchema>;
export type RelativeVolumeRead = z.infer<typeof relativeVolumeReadSchema>;
export type IvRvNumericRead = z.infer<typeof ivRvNumericReadSchema>;
export type RejectedStrategyCandidate = z.infer<typeof rejectedStrategyCandidateSchema>;
export type ConfirmationVoteTally = z.infer<typeof confirmationVoteTallySchema>;
export type LiquidityGateFacts = z.infer<typeof liquidityGateFactsSchema>;
export type MultiStrategyBestIdeaAxis = z.infer<typeof multiStrategyBestIdeaAxisSchema>;
export type MultiStrategyBestIdea = z.infer<typeof multiStrategyBestIdeaSchema>;
export type DeskIdeaExpression = z.infer<typeof deskIdeaExpressionSchema>;
export type MacroEventContext = z.infer<typeof macroEventContextSchema>;
export type MarketRegimeSnapshot = z.infer<typeof marketRegimeSnapshotSchema>;
export type MultiStrategyScreenRow = z.infer<typeof multiStrategyScreenRowSchema>;
export type MultiStrategyGroupRollup = z.infer<typeof multiStrategyGroupRollupShape>;
export type MultiStrategyClusterFlag = z.infer<typeof multiStrategyClusterFlagShape>;
export type MultiStrategyBookSummary = z.infer<typeof multiStrategyBookSummarySchema>;
export type MultiStrategyScreenList = z.infer<typeof multiStrategyScreenListSchema>;

export function decodeMultiStrategyScreen(input: unknown): MultiStrategyScreenList {
  return multiStrategyScreenListSchema.parse(input);
}

// --------------------------------------------------------------------------- //
// JS-1082: GET /api/v1/trade-ideas/screener/idea-context/{ledger_id}          //
// Mirrors services/apis/domains/trade_ideas/screener_reads.py's               //
// IdeaContextResponse EXACTLY -- `entry` is the SAME ScreenerEntry projection //
// the screener list already returns, so this REUSES                          //
// multiStrategyScreenEntrySchema rather than redeclaring it (no new          //
// derivation, no parallel shape).                                            //
// --------------------------------------------------------------------------- //

// JS-1082 round 2 (FIX 1): which durable store answered this lookup --
// mirrors services/apis/domains/trade_ideas/screener_reads.py's
// CONTEXT_SOURCE_LIVE_SCREENER / CONTEXT_SOURCE_OUTCOME_LEDGER exactly. A
// response whose `context_source` fails this enum (an unrecognized future
// value, or a malformed one) drops the WHOLE response via the top-level
// `.catch(null)` in `decodeIdeaContext` below -- never rendered as if it
// were a known tier.
export const ideaContextSourceSchema = z.enum(["LIVE_SCREENER", "OUTCOME_LEDGER"]);

// JS-1082 round 3 (FIX 1): `ticker` is a REQUIRED non-empty string here,
// deliberately tighter than the backend's own `str | None` field. The
// instrument-binding gate (TickerDashboard.tsx) needs a positive, verified
// match against the chart's route symbol before rendering ANY context; a
// response that cannot even name its own instrument (a partial/legacy
// ledger-tier document with no `listing.ticker`) fails the WHOLE decode here
// -- `decodeIdeaContext` returns `null`, which is indistinguishable from any
// other unresolvable-context case and degrades to a context-free render.
// This is intentionally NOT `.nullish()`: a null/absent ticker is not a
// "verify against nothing" case, it is a hard decode failure.
export const ideaContextResponseSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    context_source: ideaContextSourceSchema,
    ledger_id: z.string(),
    row_id: z.string().nullish(),
    ticker: z.string().min(1),
    direction: z.string().nullish(),
    generated_at: z.string().nullish(),
    entry: multiStrategyScreenEntrySchema,
  })
  .passthrough();

export type IdeaContextResponse = z.infer<typeof ideaContextResponseSchema>;

/**
 * Tolerant top-level decode for the ticker-view deep link: an invalid/
 * unresolvable/absent ledger_id (a malformed response shape, a 404 body, a
 * network error the caller already turned into a rejected promise) must
 * degrade the PAGE to a context-free render, never throw a blank page or an
 * uncaught error -- see TickerDashboard.tsx's context-resolution effect.
 * Returns `null` on ANY decode failure (group-level catch, matching the
 * house `.catch(undefined)` posture used throughout this file, just at the
 * top level since the whole response is either fully trustworthy or not
 * used at all -- there is no "partial idea context").
 */
export function decodeIdeaContext(input: unknown): IdeaContextResponse | null {
  const result = ideaContextResponseSchema.safeParse(input);
  return result.success ? result.data : null;
}

// --------------------------------------------------------------------------- //
// JS-1059/JS-1060: the immutable daily "edition" -- GET /editions/today,       //
// GET /editions/{as_of}, GET /editions (archive listing). Mirrors             //
// services/apis/domains/trade_ideas/edition.py's read models EXACTLY          //
// (_edition_detail / _edition_summary / EditionReadService.list_archive).      //
// Permissive on purpose, same posture as the screener above: `ideas` entries  //
// are the SAME snapshot ScreenerRow/ScreenerEntry shapes the screener already //
// returns, so this REUSES multiStrategyScreenRowSchema rather than            //
// redeclaring it -- an edition idea is a snapshot copy of a screener row, not //
// a distinct shape.                                                          //
// --------------------------------------------------------------------------- //

// regime_summary: deterministic FACTS ONLY (see edition.py's
// compute_regime_summary) -- plain buckets keyed by string, sorted
// (desc count, key) server-side. Permissive: an unmapped bucket key still
// decodes since this is a Record, not an enum.
const editionBucketCountsSchema = z.record(z.string(), z.number().int().nonnegative());

// Adversarial-review BLOCKER 1: `idea_count` is otherwise required, but the
// backend's own documented fallback (edition._edition_detail's
// `document.get("regime_summary") or {}`) can legitimately emit a bare `{}`
// for a pre-JS-1059 or malformed stored document. `{}` is honest-absent, not
// malformed -- default `idea_count` to 0 so the group decodes to the exact
// same "zero curated ideas" shape a caller can already produce explicitly,
// rather than throwing out of `.parse()` and blanking the whole page.
export const editionRegimeSummarySchema = z
  .object({
    idea_count: z.number().int().nonnegative().default(0),
    by_iv_rv_regime: editionBucketCountsSchema.default({}),
    by_iv_rank_band: editionBucketCountsSchema.default({}),
    by_direction: editionBucketCountsSchema.default({}),
    by_archetype_family: editionBucketCountsSchema.default({}),
    // JS-1066: additive -- OMITTED entirely by the backend (never a bare
    // `{}`) whenever no curated row carries a `discovery_lane` at all (see
    // edition.compute_regime_summary's docstring). Defaulted to `{}` here
    // the same way the other buckets are: `editionRegimeLine` already
    // renders an absent/empty bucket as "no clause" either way.
    by_discovery_lane: editionBucketCountsSchema.default({}),
    // JS-1068: additive, market-WIDE (not derived from `rows` at all) --
    // OMITTED entirely by the backend when no market-regime snapshot was
    // resolved for this edition's as_of (see
    // edition.compute_regime_summary's own `market_regime` docstring).
    // Tolerant: a malformed snapshot drops the masthead clause only.
    market_regime: tolerantMarketRegimeSnapshot,
    // JS-1068 (adversarial-review FIX 3): present ONLY when the curated
    // rows' own market_regime_at_publication stamps DISAGREED (a genuine
    // anomaly, e.g. a partial sweep spanning two resolution outcomes) --
    // see edition.MARKET_REGIME_DISAGREEMENT / compute_regime_summary's
    // docstring. Mutually exclusive with `market_regime` above: exactly one
    // (or neither, when no row carried the field at all) is ever present.
    market_regime_absent_reason: z.string().nullish(),
  })
  .passthrough();

// scoreboard_delta: honestly-empty-until-JS-1061 shape (see
// edition.py's compute_scoreboard_delta) -- `running_record` is a fixed
// WON/LOST/EXPIRED tally, always present with every key at 0 until the
// resolution job exists.
const editionResolvedEntrySchema = z
  .object({
    ledger_id: z.string().nullish(),
    listing: z.unknown().nullish(),
    archetype: z.string().nullish(),
    direction: z.string().nullish(),
    status: z.string().nullish(),
    resolution: z.unknown().nullish(),
  })
  .passthrough();

// Adversarial-review BLOCKER 1: the backend's own documented fallback
// (edition._edition_detail's `document.get("scoreboard_delta") or {}`) can
// legitimately emit a bare `{}`. Unlike book_summary, this group's OWN
// all-zero defaults already ARE the honest-empty representation
// (ScoreboardStrip's empty state keys off `running_record_total === 0`), so
// defaulting the two previously-required counters lets `{}` decode directly
// into that same shape rather than needing a separate null branch.
export const editionScoreboardDeltaSchema = z
  .object({
    resolved_since_previous: z.array(editionResolvedEntrySchema).default([]),
    resolved_since_previous_count: z.number().int().nonnegative().default(0),
    running_record: z.record(z.string(), z.number().int().nonnegative()).default({}),
    running_record_total: z.number().int().nonnegative().default(0),
  })
  .passthrough();

// Adversarial-review SHOULD-FIX 3: `ideas` is per-row tolerant -- ONE
// malformed snapshot (a shape this schema didn't anticipate) must never
// throw out of `.parse()` and blank the WHOLE edition, same posture as the
// screener's own tolerant-enrichment pattern above, just one level up (per
// ROW instead of per enrichment GROUP). A row that fails
// `multiStrategyScreenRowSchema` is dropped, never silently -- the dropped
// count rides alongside the surviving rows so the page can render an honest
// "N idea(s) could not be displayed" note instead of either a full-page
// error or a quietly-shorter list.
const editionIdeasWithDropsSchema = z
  .array(z.unknown())
  .default([])
  .transform((rawItems) => {
    const ideas: MultiStrategyScreenRow[] = [];
    let droppedCount = 0;
    for (const raw of rawItems) {
      const result = multiStrategyScreenRowSchema.safeParse(raw);
      if (result.success) {
        ideas.push(result.data);
      } else {
        droppedCount += 1;
      }
    }
    return { ideas, droppedCount };
  });

// DESK-013 (JS-1150), ruling 3: the candidate funnel -- what the edition read
// and what held every candidate it did not carry
// (domains.trade_ideas.edition_funnel.build_candidate_funnel).
//
// Every field is nullish/defaulted, because an edition minted BEFORE this
// ticket honestly carries no funnel at all: the backend emits `{}` for such a
// record (see edition._edition_detail) rather than fabricating one, and the
// page must render that as "this edition does not record its funnel" instead
// of as a funnel full of zeros. `emptyObjectToUndefined` gives exactly that
// distinction, the same way book_summary already uses it.
const editionFunnelPerTickerSchema = z
  .object({
    ticker: z.string(),
    direction: z.string().nullish(),
    outcome: z.string(),
    // The row's OWN honest evaluation instant -- a row protected by the
    // displacement rule speaks as of when IT was priced, not as of the
    // edition cut, so the page can say "quotes as of ..." truthfully.
    evaluated_at: z.string().nullish(),
    priced: z.boolean().nullish(),
    hold_reason_codes: z.array(z.string()).default([]),
    hold_kind: z.string().nullish(),
  })
  .passthrough();

export const editionCandidateFunnelSchema = z
  .object({
    schema_version: z.string().nullish(),
    rows_read: z.number().int().nonnegative().nullish(),
    candidates_considered: z.number().int().nonnegative().nullish(),
    included_count: z.number().int().nonnegative().nullish(),
    held_count: z.number().int().nonnegative().nullish(),
    hold_reason_histogram: z.record(z.string(), z.number().int().nonnegative()).default({}),
    // Per-row tolerant, matching `editionIdeasWithDropsSchema`'s posture: one
    // malformed candidate entry must never throw out of `.parse()` and blank
    // the whole explanation, which is precisely what the owner needs most on
    // an empty edition.
    per_ticker: z
      .array(z.unknown())
      .default([])
      .transform((rawItems) => {
        const rows: z.infer<typeof editionFunnelPerTickerSchema>[] = [];
        for (const raw of rawItems) {
          const result = editionFunnelPerTickerSchema.safeParse(raw);
          if (result.success) rows.push(result.data);
        }
        return rows;
      }),
    quality_bar: z.string().nullish(),
    freshness_window_seconds: z.number().int().nonnegative().nullish(),
    displacement_policy_version: z.string().nullish(),
  })
  .passthrough();

// Detail read: GET /editions/today, GET /editions/{as_of}
// (domains.trade_ideas.edition._edition_detail).
export const editionDetailSchema = z
  .object({
    schema_version: z.string(),
    edition_id: z.string().nullish(),
    content_hash: z.string().nullish(),
    as_of: z.string().date(),
    edition_number: z.number().int().nonnegative().nullish(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    regime_summary: editionRegimeSummarySchema,
    // Snapshot copies of the curated screener rows -- REUSES the screener's
    // own row schema rather than redeclaring it (see module comment above).
    // Per-row tolerant: see editionIdeasWithDropsSchema.
    ideas: editionIdeasWithDropsSchema,
    book_summary: z.preprocess(emptyObjectToUndefined, editionBookSummarySchema.nullish()),
    scoreboard_delta: editionScoreboardDeltaSchema,
    generated_at: z.string().nullish(),
    // DESK-013 ruling 3. `{}` (a pre-ticket edition) decodes to undefined, so
    // the page can tell "no funnel recorded" from "a funnel that found
    // nothing" -- two very different statements about the same empty page.
    candidate_funnel: z.preprocess(
      emptyObjectToUndefined,
      editionCandidateFunnelSchema.nullish(),
    ),
  })
  .passthrough()
  .transform((edition) => ({
    ...edition,
    ideas: edition.ideas.ideas,
    ideas_dropped_count: edition.ideas.droppedCount,
  }));

// Archive listing row: GET /editions (domains.trade_ideas.edition._edition_summary).
// Deliberately NEVER carries `ideas` -- the server-side projection omits it.
export const editionArchiveItemSchema = z
  .object({
    as_of: z.string().date().nullish(),
    edition_number: z.number().int().nonnegative().nullish(),
    idea_count: z.number().int().nonnegative(),
    content_hash: z.string().nullish(),
    generated_at: z.string().nullish(),
  })
  .passthrough();

export const editionArchiveListSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    count: z.number().int().nonnegative(),
    items: z.array(editionArchiveItemSchema).default([]),
  })
  .passthrough();

export type EditionRegimeSummary = z.infer<typeof editionRegimeSummarySchema>;
export type EditionGroupRollup = z.infer<typeof multiStrategyGroupRollupShape>;
export type EditionClusterFlag = z.infer<typeof multiStrategyClusterFlagShape>;
export type EditionBookSummary = z.infer<typeof editionBookSummarySchema>;
export type EditionResolvedEntry = z.infer<typeof editionResolvedEntrySchema>;
export type EditionScoreboardDelta = z.infer<typeof editionScoreboardDeltaSchema>;
export type EditionCandidateFunnel = z.infer<typeof editionCandidateFunnelSchema>;
export type EditionDetail = z.infer<typeof editionDetailSchema>;
export type EditionArchiveItem = z.infer<typeof editionArchiveItemSchema>;
export type EditionArchiveList = z.infer<typeof editionArchiveListSchema>;

export function decodeEditionDetail(input: unknown): EditionDetail {
  return editionDetailSchema.parse(input);
}

export function decodeEditionArchiveList(input: unknown): EditionArchiveList {
  return editionArchiveListSchema.parse(input);
}

// --------------------------------------------------------------------------- //
// JS-1090b: audit-grade outcome-ledger rows + performance aggregates + the     //
// edition integrity trail (services/apis/domains/trade_ideas/                 //
// outcome_ledger_reads.py, edition.py's EditionReadService.get_integrity_trail).
//
// Every numeric field on these three response shapes is projected server-side
// as an already-stringified value (see outcome_ledger_reads.py's `_to_str`
// helper -- no Pydantic field in that module is typed `Decimal`; the Decimal
// -> str conversion happens BEFORE model construction). They map onto this
// file's existing `decimalString` shape.
//
// Tolerance posture (mirrors multiStrategyExecutionFrictionShape /
// editionIdeasWithDropsSchema above): a ledger row is per-row tolerant (a
// malformed row is dropped, count surfaced) and each of the performance
// response's independent sub-blocks is its OWN `.catch(undefined)`-wrapped
// group, so one malformed section (e.g. a broken `transaction_cost_disclosure`
// dict) degrades to `undefined` for just that field rather than blanking the
// whole performance page.
// --------------------------------------------------------------------------- //

const LEDGER_STATUS_VALUES = ["OPEN", "RESOLVED", "FLAGGED"] as const;

// LedgerRow.exit_rule: a raw dict[str, Any] server-side, no enforced shape --
// this is the SAME loose shape `ExitRuleFacts` (screenerLabels.ts) already
// expects, so `exitRuleCopy()` applies directly. Passthrough + all-nullish so
// an unrecognized field never fails decode.
const ledgerExitRuleShape = z
  .object({
    target_kind: z.string().nullish(),
    target_value: z.string().nullish(),
    stop_kind: z.string().nullish(),
    stop_value: z.string().nullish(),
    dte_floor: z.number().nullish(),
  })
  .passthrough();

export const ledgerRowSchema = z
  .object({
    ledger_id: z.string().min(1),
    ticker: z.string().nullish(),
    listing: z.record(z.string(), z.unknown()).nullish(),
    archetype: z.string().nullish(),
    direction: z.string().nullish(),
    strikes: z.array(z.string()).default([]),
    expiry: z.string().nullish(),
    entry_timestamp: z.string().nullish(),
    entry_reference_price: decimalString.nullish(),
    entry_type: z.string().nullish(),
    exit_rule: ledgerExitRuleShape.nullish().catch(undefined),
    exit_rule_policy_version: z.string().nullish(),
    status: z.string(),
    status_diagnostic: z.string().nullish(),
    resolution_timestamp: z.string().nullish(),
    resolution_triggering_condition: z.string().nullish(),
    exit_reference_price: decimalString.nullish(),
    net_pnl_usd: decimalString.nullish(),
    fill_model: z.string().nullish(),
    data_coverage_note: z.string().nullish(),
    resolution_policy_version: z.string().nullish(),
    observation_cadence: z.string().nullish(),
    intraday_touches_unobserved: z.boolean().nullish(),
    best_idea_score: decimalString.nullish(),
    score_policy_versions: z.record(z.string(), z.unknown()).nullish(),
    discovery_lane: z.string().nullish(),
    authority_label: z.string().nullish(),
    edition_published_at: z.string().nullish(),
    edition_as_of: z.string().nullish(),
  })
  .passthrough();

export const ledgerRowFiltersSchema = z
  .object({
    status: z.string().nullish(),
    archetype: z.string().nullish(),
    direction: z.string().nullish(),
    lane: z.string().nullish(),
    date_from: z.string().nullish(),
    date_to: z.string().nullish(),
  })
  .passthrough();

// Per-row tolerant array: mirrors editionIdeasWithDropsSchema -- one malformed
// row is dropped, never the whole rows response.
const ledgerRowsWithDropsSchema = z
  .array(z.unknown())
  .default([])
  .transform((rawItems) => {
    const items: LedgerRow[] = [];
    let droppedCount = 0;
    for (const raw of rawItems) {
      const result = ledgerRowSchema.safeParse(raw);
      if (result.success) {
        items.push(result.data);
      } else {
        droppedCount += 1;
      }
    }
    return { items, droppedCount };
  });

export const ledgerRowsResponseSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    count: z.number().int().nonnegative(),
    filters: ledgerRowFiltersSchema,
    items: ledgerRowsWithDropsSchema,
    next_cursor: z.string().nullish(),
  })
  .passthrough()
  .transform((response) => ({
    ...response,
    items: response.items.items,
    items_dropped_count: response.items.droppedCount,
  }));

// --------------------------------------------------------------------------- //
// Performance aggregates -- each sub-block below is exported standalone (for
// the tolerant `.catch(undefined)` wrapping applied where referenced) so a
// malformed section never takes down the rest of the page.
// --------------------------------------------------------------------------- //

export const exclusionBreakdownShape = z
  .object({
    total: z.number().int().nonnegative().default(0),
    by_reason: z.record(z.string(), z.number().int().nonnegative()).default({}),
  })
  .passthrough();
const tolerantExclusionBreakdown = exclusionBreakdownShape.default({ total: 0, by_reason: {} });

export const populationReconciliationShape = z
  .object({
    total_documents: z.number().int().nonnegative(),
    open_count: z.number().int().nonnegative(),
    resolved_count: z.number().int().nonnegative(),
    flagged_count: z.number().int().nonnegative(),
    unknown_status_count: z.number().int().nonnegative(),
    unknown_status_note: z.string(),
  })
  .passthrough();

export const pnlStatsShape = z
  .object({
    n: z.number().int().nonnegative(),
    excluded: tolerantExclusionBreakdown,
    total_pnl_usd: decimalString.nullish(),
    mean_pnl_usd: decimalString.nullish(),
    median_pnl_usd: decimalString.nullish(),
    win_count: z.number().int().nonnegative().default(0),
    loss_count: z.number().int().nonnegative().default(0),
    breakeven_count: z.number().int().nonnegative().default(0),
    decided_n: z.number().int().nonnegative().default(0),
    decided_trade_win_rate: decimalString.nullish(),
  })
  .passthrough();

export const returnOnRiskExcludedSampleShape = z
  .object({
    ledger_ids: z.array(z.string()).default([]),
    total_excluded: z.number().int().nonnegative().default(0),
    disclosed_count: z.number().int().nonnegative().default(0),
    by_reason: z.record(z.string(), z.number().int().nonnegative()).default({}),
  })
  .passthrough();
const tolerantReturnOnRiskExcludedSample = returnOnRiskExcludedSampleShape.default({
  ledger_ids: [],
  total_excluded: 0,
  disclosed_count: 0,
  by_reason: {},
});

export const returnOnRiskStatsShape = z
  .object({
    n: z.number().int().nonnegative(),
    excluded: tolerantReturnOnRiskExcludedSample,
    excluded_reason: z.string().nullish(),
    // mean/median are PER-TRADE ratios; aggregate is the POOLED ratio -- these
    // are different numbers and must never be conflated in the UI (per the
    // ticket). Kept as distinct fields, never merged/aliased here.
    mean_return_on_risk_pct: decimalString.nullish(),
    median_return_on_risk_pct: decimalString.nullish(),
    aggregate_return_on_risk_pct: decimalString.nullish(),
    aggregate_return_on_risk_note: z.string().default(""),
  })
  .passthrough();

export const distributionBucketShape = z
  .object({
    bucket: z.string(),
    count: z.number().int().nonnegative(),
  })
  .passthrough();

export const distributionStatsShape = z
  .object({
    n: z.number().int().nonnegative(),
    excluded: tolerantExclusionBreakdown,
    buckets: z.array(distributionBucketShape).default([]),
  })
  .passthrough();

export const drawdownPointShape = z
  .object({
    ledger_id: z.string().nullable(),
    label: z.string().nullish(),
    resolved_at: z.string().nullable(),
    cumulative_pnl_usd: decimalString,
  })
  .passthrough();

export const drawdownStatsShape = z
  .object({
    n: z.number().int().nonnegative(),
    excluded: tolerantExclusionBreakdown,
    ordering_convention: z.string().default(""),
    max_drawdown_usd: decimalString.nullish(),
    max_cumulative_pnl_usd: decimalString.nullish(),
    final_cumulative_pnl_usd: decimalString.nullish(),
    peak_point: drawdownPointShape.nullish(),
    trough_point: drawdownPointShape.nullish(),
  })
  .passthrough();

export const holdingPeriodStatsShape = z
  .object({
    n: z.number().int().nonnegative(),
    excluded: tolerantExclusionBreakdown,
    mean_days: decimalString.nullish(),
    median_days: decimalString.nullish(),
  })
  .passthrough();

export const cohortAggregateShape = z
  .object({
    cohort_key: z.string(),
    resolved_population: z.number().int().nonnegative(),
    n: z.number().int().nonnegative(),
    low_sample: z.boolean(),
    pnl: pnlStatsShape,
    return_on_risk: returnOnRiskStatsShape,
    expectancy_usd: decimalString.nullish(),
  })
  .passthrough();
// A cohort row that fails its own schema is dropped from the cut, never
// thrown out of the whole performance response -- same per-row tolerance as
// ledger rows above, one level down into each `by_*` cohort array.
const tolerantCohortAggregateList = z
  .array(z.unknown())
  .default([])
  .transform((rawItems) => {
    const items: CohortAggregate[] = [];
    for (const raw of rawItems) {
      const result = cohortAggregateShape.safeParse(raw);
      if (result.success) items.push(result.data);
    }
    return items;
  });

export const scanTelemetryShape = z
  .object({
    documents_scanned: z.number().int().nonnegative(),
    elapsed_ms: z.number().int().nonnegative(),
    max_scan_documents: z.number().int().nonnegative(),
  })
  .passthrough();

// Each sub-block is wrapped `.catch(undefined)` at the point it's referenced
// on the response object -- a malformed pnl/return_on_risk/distribution/
// drawdown/holding_period/transaction_cost_disclosure/scan section degrades
// independently rather than failing `.parse()` for the entire response.
const tolerantPopulation = populationReconciliationShape.nullish().catch(undefined);
const tolerantPnlStats = pnlStatsShape.nullish().catch(undefined);
const tolerantReturnOnRiskStats = returnOnRiskStatsShape.nullish().catch(undefined);
const tolerantDistributionStats = distributionStatsShape.nullish().catch(undefined);
const tolerantDrawdownStats = drawdownStatsShape.nullish().catch(undefined);
const tolerantHoldingPeriodStats = holdingPeriodStatsShape.nullish().catch(undefined);
const tolerantScanTelemetry = scanTelemetryShape.nullish().catch(undefined);
// transaction_cost_disclosure is an opaque Record<string, unknown> of
// already-formatted prose -- render verbatim, never reformat. Tolerant: a
// non-object/garbage payload degrades to undefined rather than crashing the
// disclosure block.
const tolerantTransactionCostDisclosure = z
  .record(z.string(), z.unknown())
  .nullish()
  .catch(undefined);

export const performanceAggregatesResponseSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    population: tolerantPopulation,
    // Round 2 (adversarial review, HIGH FIX 3 -- zero-resolved accounting):
    // REQUIRED, no `.default(0)`. A `.default(0)` here meant a genuinely
    // MISSING `resolved_count` on a malformed/truncated response silently
    // decoded as "zero resolved" -- indistinguishable from the HONEST
    // zero-resolved case (a real, healthy ledger with every entry still
    // OPEN/FLAGGED/unknown-status). Missing this field now fails
    // `.parse()` outright, surfacing as the section's own decode-failure
    // state (LedgerPage/PerformancePage's `performanceQuery.isError`
    // handling) -- never a defaulted zero that could be mistaken for a
    // real, reconciled "nothing has resolved yet" population.
    resolved_count: z.number().int().nonnegative(),
    open_count: z.number().int().nonnegative().default(0),
    flagged_count: z.number().int().nonnegative().default(0),
    censored_note: z.string().default(""),
    expired_outcome_definition: z.string().default(""),
    outcome_kind_counts: z.record(z.string(), z.number().int().nonnegative()).default({}),
    pnl: tolerantPnlStats,
    return_on_risk: tolerantReturnOnRiskStats,
    expectancy_usd: decimalString.nullish(),
    expectancy_note: z.string().default(""),
    distribution: tolerantDistributionStats,
    drawdown: tolerantDrawdownStats,
    holding_period: tolerantHoldingPeriodStats,
    min_cohort_sample_size: z.number().int().nonnegative().default(5),
    by_archetype: tolerantCohortAggregateList,
    by_direction: tolerantCohortAggregateList,
    by_lane: tolerantCohortAggregateList,
    by_score_band: tolerantCohortAggregateList,
    by_publication_month: tolerantCohortAggregateList,
    transaction_cost_disclosure: tolerantTransactionCostDisclosure,
    scan: tolerantScanTelemetry,
  })
  .passthrough();

// --------------------------------------------------------------------------- //
// Edition integrity trail: GET /api/v1/trade-ideas/editions/{as_of}/integrity
// (domains.trade_ideas.edition.EditionReadService.get_integrity_trail). No
// response_model is declared server-side (bare `dict` return type) -- this
// schema is this feature's only enforcement of the shape, so it stays
// reasonably strict on the fields the UI actually renders and passthrough on
// the rest.
// --------------------------------------------------------------------------- //

export const editionIntegrityHashContractEraSchema = z.enum(["v1_with_idea_count", "v1_pre_idea_count"]);

export const editionIntegrityStatusSchema = z.enum(["VERIFIED", "MISMATCH", "LEGACY_UNVERIFIABLE"]);

export const editionIntegrityTrailSchema = z
  .object({
    schema_version: z.string(),
    authority: z.string().default("INTERNAL_SHADOW_RESEARCH"),
    as_of: z.string(),
    edition_id: z.string().nullish(),
    edition_number: z.number().int().nonnegative().nullish(),
    hash_contract_era: editionIntegrityHashContractEraSchema.nullish(),
    hash_verification_status: editionIntegrityStatusSchema,
    stored_content_hash: z.string().nullish(),
    recomputed_content_hash: z.string().nullish(),
    hash_verified: z.boolean(),
    hash_verification_failure_reason: z.string().nullish(),
    // The exact bytes that were hashed -- arbitrary JSON, rendered verbatim
    // (e.g. via the client-side "download canonical payload" affordance).
    // Never re-validated field-by-field here: it is opaque provenance data,
    // not a shape this UI interprets.
    canonical_payload: z.unknown(),
    prior_edition_as_of: z.string().nullish(),
    prior_edition_content_hash: z.string().nullish(),
    generated_at: z.string().nullish(),
  })
  .passthrough();

export type LedgerExitRule = z.infer<typeof ledgerExitRuleShape>;
export type LedgerRow = z.infer<typeof ledgerRowSchema>;
export type LedgerRowFilters = z.infer<typeof ledgerRowFiltersSchema>;
export type LedgerRowsResponse = z.infer<typeof ledgerRowsResponseSchema>;
export type ExclusionBreakdown = z.infer<typeof exclusionBreakdownShape>;
export type PopulationReconciliation = z.infer<typeof populationReconciliationShape>;
export type PnlStats = z.infer<typeof pnlStatsShape>;
export type ReturnOnRiskExcludedSample = z.infer<typeof returnOnRiskExcludedSampleShape>;
export type ReturnOnRiskStats = z.infer<typeof returnOnRiskStatsShape>;
export type DistributionBucket = z.infer<typeof distributionBucketShape>;
export type DistributionStats = z.infer<typeof distributionStatsShape>;
export type DrawdownPoint = z.infer<typeof drawdownPointShape>;
export type DrawdownStats = z.infer<typeof drawdownStatsShape>;
export type HoldingPeriodStats = z.infer<typeof holdingPeriodStatsShape>;
export type CohortAggregate = z.infer<typeof cohortAggregateShape>;
export type ScanTelemetry = z.infer<typeof scanTelemetryShape>;
export type PerformanceAggregatesResponse = z.infer<typeof performanceAggregatesResponseSchema>;
export type EditionIntegrityTrail = z.infer<typeof editionIntegrityTrailSchema>;

export function decodeLedgerRows(input: unknown): LedgerRowsResponse {
  return ledgerRowsResponseSchema.parse(input);
}

export function decodeLedgerPerformance(input: unknown): PerformanceAggregatesResponse {
  return performanceAggregatesResponseSchema.parse(input);
}

export function decodeEditionIntegrityTrail(input: unknown): EditionIntegrityTrail {
  return editionIntegrityTrailSchema.parse(input);
}

export const LEDGER_STATUS_FILTER_VALUES = LEDGER_STATUS_VALUES;
